import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import analytics, { EVENTS } from '../services/analytics';

// PRICING
const FRAME_PRICE_PER_CM2 = 0.13; // EUR per cm² (HT) — was 0.20, reduced by 35% per client request (2026-09-16)

// VAT CONFIGURATION
const FRAME_VAT_RATE = 0.055; // 5.5% VAT for frame price
const SHIPPING_VAT_RATE = 0.20; // 20% VAT for transport
const PRICE_ROUNDING_STEP = 10; // Round payable prices to nearest 10 EUR

// EU COUNTRIES LIST (after Brexit, UK is NOT in EU)
const EU_COUNTRIES = [
  'France', 'Allemagne', 'Belgique', 'Pays-Bas',
  'Italie', 'Espagne', 'Portugal', 'Autriche',
  'Danemark', 'Suède', 'Finlande', 'République d\'Irlande',
  'Pologne', 'République tchèque', 'Hongrie',
  'Roumanie', 'Bulgarie', 'Grèce', 'Croatie',
  'Slovénie', 'Slovaquie', 'Lituanie',
  'Lettonie', 'Estonie', 'Luxembourg', 'Malte', 'Chypre',
  // France Overseas are part of France
  'Guadeloupe', 'Martinique', 'La Réunion',
  'Guyane', 'Mayotte', 'Saint-Pierre-et-Miquelon',
  'Nouvelle-Calédonie', 'Polynésie Française',
  'Wallis-et-Futuna', 'Terres Australes et Antarctiques Françaises'
];

// Helper function
function isEUCountry(country) {
  return EU_COUNTRIES.includes(country);
}

function roundToNearestTen(amount) {
  return Math.round((Number(amount) || 0) / PRICE_ROUNDING_STEP) * PRICE_ROUNDING_STEP;
}

function formatVatRate(rate) {
  const percent = rate * 100;
  return Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(1);
}

// SHIPPING ZONES & COUNTRIES
const FRANCE_METRO = {
  zone: 'FRANCE',
  countries: ['France']
};

const FRANCE_OM1 = {
  zone: 'OM1',
  countries: [
    'Guadeloupe', 'Martinique', 'La Réunion',
    'Guyane', 'Mayotte', 'Saint-Pierre-et-Miquelon'
  ]
};

const FRANCE_OM2 = {
  zone: 'OM2',
  countries: [
    'Nouvelle-Calédonie', 'Polynésie Française',
    'Wallis-et-Futuna', 'Terres Australes et Antarctiques Françaises'
  ]
};

const ZONE_1 = {
  zone: 'ZONE_1',
  countries: ['Allemagne', 'Belgique', 'Luxembourg', 'Pays-Bas']
};

const ZONE_2 = {
  zone: 'ZONE_2',
  countries: [
    'Autriche', 'Espagne', 'Guernesey', 'Italie',
    'Jersey', 'Portugal', 'République d\'Irlande', 'Royaume-Uni'
  ]
};

const ZONE_3 = {
  zone: 'ZONE_3',
  countries: [
    'Danemark', 'Estonie', 'Groenland', 'Hongrie',
    'Îles Féroé', 'Lettonie', 'Liechtenstein', 'Lituanie',
    'Pologne', 'République tchèque', 'Slovaquie', 'Slovénie',
    'Suède', 'Suisse'
  ]
};

const ZONE_4 = {
  zone: 'ZONE_4',
  countries: [
    'Bulgarie', 'Chypre', 'Croatie', 'Finlande', 'Grèce',
    'Islande', 'Malte', 'Norvège', 'Roumanie', 'Turquie',
    'Maroc', 'Algérie', 'Tunisie'
  ]
};

const ZONE_5 = {
  zone: 'ZONE_5',
  countries: [
    'Australie', 'Canada', 'Chine', 'Corée du Sud',
    'États-Unis', 'Hong Kong', 'Inde', 'Israël',
    'Japon', 'Russie', 'Singapour', 'Taiwan',
    'Thaïlande', 'Vietnam'
  ]
};

const ZONE_6 = {
  zone: 'ZONE_6',
  countries: ['Autres pays']
};

// ALL ZONES FOR LOOKUP
const ALL_ZONES = [FRANCE_METRO, FRANCE_OM1, FRANCE_OM2, ZONE_1, ZONE_2, ZONE_3, ZONE_4, ZONE_5, ZONE_6];

// SHIPPING RATE TABLES (EUR, HT)
const FRANCE_RATES = {
  0.50: 8.62, 1.00: 11.39, 2.00: 11.34, 3.00: 12.34, 4.00: 13.37,
  5.00: 14.35, 6.00: 14.97, 7.00: 15.94, 8.00: 16.91, 9.00: 17.92,
  10.00: 18.90, 11.00: 19.51, 12.00: 20.47, 13.00: 21.42, 14.00: 22.42,
  15.00: 23.38, 16.00: 24.33, 17.00: 25.29, 18.00: 26.26, 19.00: 27.23,
  20.00: 28.18, 21.00: 28.88, 22.00: 29.82, 23.00: 30.79, 24.00: 31.75,
  25.00: 32.67, 26.00: 33.65, 27.00: 34.58, 28.00: 35.54, 29.00: 36.53,
  30.00: 37.44
};

const OM1_RATES = {
  0.50: 15.59, 1.00: 19.99, 2.00: 26.96, 3.00: 33.92, 4.00: 39.41,
  5.00: 45.73, 6.00: 54.81, 7.00: 61.78, 8.00: 65.73, 9.00: 72.40,
  10.00: 79.05, 15.00: 112.35, 20.00: 140.08, 25.00: 170.99, 30.00: 203.29
};

const OM2_RATES = {
  0.50: 22.87, 1.00: 28.30, 2.00: 44.03, 3.00: 62.00, 4.00: 74.90,
  5.00: 90.51, 6.00: 110.98, 7.00: 124.90, 8.00: 137.34, 9.00: 152.96,
  10.00: 168.55, 15.00: 253.85, 20.00: 320.42, 25.00: 394.17, 30.00: 471.04
};

const ZONE_1_GERMANY_RATES = {
  0.50: 9.28, 1.00: 11.33, 2.00: 12.13, 3.00: 13.01, 4.00: 13.90,
  5.00: 14.71, 6.00: 15.52, 7.00: 16.33, 8.00: 17.15, 9.00: 17.97,
  10.00: 18.79, 15.00: 23.44, 20.00: 28.10, 25.00: 32.77, 30.00: 37.44
};

const ZONE_1_BELGIUM_RATES = {
  0.50: 9.10, 1.00: 11.11, 2.00: 11.89, 3.00: 12.76, 4.00: 13.61,
  5.00: 14.41, 6.00: 15.21, 7.00: 16.01, 8.00: 16.81, 9.00: 17.61,
  10.00: 18.41, 15.00: 22.98, 20.00: 27.54, 25.00: 32.13, 30.00: 36.70
};

const ZONE_1_OTHER_RATES = {
  0.50: 9.33, 1.00: 11.39, 2.00: 12.19, 3.00: 13.07, 4.00: 13.97,
  5.00: 14.78, 6.00: 15.60, 7.00: 16.41, 8.00: 17.23, 9.00: 18.06,
  10.00: 18.88, 15.00: 23.55, 20.00: 28.24, 25.00: 32.94, 30.00: 37.63
};

const ZONE_2_UK_RATES = {
  0.50: 10.67, 1.00: 13.56, 2.00: 15.17, 3.00: 16.16, 4.00: 17.04,
  5.00: 17.97, 6.00: 18.84, 7.00: 19.84, 8.00: 20.71, 9.00: 21.57,
  10.00: 22.46, 15.00: 27.30, 20.00: 32.03, 25.00: 36.73, 30.00: 41.47
};

const ZONE_2_ITALY_RATES = {
  0.50: 10.50, 1.00: 13.36, 2.00: 14.94, 3.00: 15.92, 4.00: 16.79,
  5.00: 17.70, 6.00: 18.56, 7.00: 19.54, 8.00: 20.39, 9.00: 21.25,
  10.00: 22.13, 15.00: 26.90, 20.00: 31.55, 25.00: 36.19, 30.00: 40.85
};

const ZONE_2_SPAIN_RATES = {
  0.50: 10.45, 1.00: 13.29, 2.00: 14.87, 3.00: 15.84, 4.00: 16.71,
  5.00: 17.61, 6.00: 18.46, 7.00: 19.45, 8.00: 20.29, 9.00: 21.15,
  10.00: 22.02, 15.00: 26.77, 20.00: 31.39, 25.00: 36.01, 30.00: 40.65
};

const ZONE_2_OTHER_RATES = {
  0.50: 10.72, 1.00: 13.63, 2.00: 15.24, 3.00: 16.24, 4.00: 17.12,
  5.00: 18.06, 6.00: 18.93, 7.00: 19.93, 8.00: 20.81, 9.00: 21.68,
  10.00: 22.56, 15.00: 27.44, 20.00: 32.18, 25.00: 36.91, 30.00: 41.67
};

const ZONE_3_SWITZERLAND_RATES = {
  0.50: 11.88, 1.00: 14.77, 2.00: 16.51, 3.00: 18.00, 4.00: 19.82,
  5.00: 21.66, 6.00: 28.52, 7.00: 30.15, 8.00: 31.66, 9.00: 33.19,
  10.00: 34.68, 15.00: 43.27, 20.00: 51.72, 25.00: 60.23, 30.00: 62.63
};

const ZONE_3_OTHER_RATES = {
  0.50: 11.94, 1.00: 14.84, 2.00: 16.60, 3.00: 18.09, 4.00: 19.91,
  5.00: 21.76, 6.00: 28.66, 7.00: 30.30, 8.00: 31.82, 9.00: 33.34,
  10.00: 34.86, 15.00: 43.48, 20.00: 51.98, 25.00: 60.51, 30.00: 62.94
};

const ZONE_4_RATES = {
  0.50: 13.99, 1.00: 17.11, 2.00: 18.84, 3.00: 20.66, 4.00: 23.10,
  5.00: 27.93, 6.00: 31.60, 7.00: 36.00, 8.00: 41.35, 9.00: 44.45,
  10.00: 48.24, 15.00: 65.24, 20.00: 76.54, 25.00: 90.66, 30.00: 105.18
};

const ZONE_5_USA_RATES = {
  0.50: 26.58, 1.00: 29.39, 2.00: 40.31, 3.00: 51.21, 4.00: 58.68,
  5.00: 62.64, 6.00: 80.07, 7.00: 92.71, 8.00: 102.71, 9.00: 107.03,
  10.00: 112.37, 15.00: 164.54, 20.00: 180.19, 25.00: 231.30, 30.00: 266.56
};

const ZONE_5_CHINA_RATES = {
  0.50: 26.85, 1.00: 29.69, 2.00: 40.71, 3.00: 51.72, 4.00: 59.29,
  5.00: 63.28, 6.00: 80.89, 7.00: 93.65, 8.00: 103.76, 9.00: 108.12,
  10.00: 113.51, 15.00: 166.22, 20.00: 182.03, 25.00: 233.66, 30.00: 269.28
};

const ZONE_5_OTHER_RATES = {
  0.50: 27.53, 1.00: 30.44, 2.00: 41.74, 3.00: 53.02, 4.00: 60.78,
  5.00: 64.87, 6.00: 82.93, 7.00: 96.01, 8.00: 106.36, 9.00: 110.85,
  10.00: 116.37, 15.00: 170.40, 20.00: 186.62, 25.00: 239.54, 30.00: 276.05
};

const ZONE_6_RATES = {
  0.50: 31.57, 1.00: 37.14, 2.00: 50.99, 3.00: 65.13, 4.00: 78.99,
  5.00: 90.82, 6.00: 104.33, 7.00: 117.97, 8.00: 131.50, 9.00: 145.34,
  10.00: 158.87, 15.00: 220.12, 20.00: 282.72, 25.00: 343.96, 30.00: 405.23
};

// ADDITIONAL FEES
const FEES = {
  DECARBONATION: 0.03,
  NON_MECHANIZABLE: 6.50,
  UK_SUPPLEMENT: 4.40,
  USA_SUPPLEMENT: 5.00,
  CHINA_SUPPLEMENT: 6.00,
  SECURITY: 0.15
};

// ========================================
// DHL EXPRESS SHIPPING RATES & ZONES
// ========================================

// DHL Zone mapping (country name -> DHL zone)
const DHL_ZONES = {
  // Zone A - France Domestic
  'France': 'A',
  // Zone 1
  'Allemagne': 1, 'Belgique': 1, 'Espagne': 1, 'Italie': 1,
  'Luxembourg': 1, 'Pays-Bas': 1, 'Royaume-Uni': 1,
  // Zone 2
  'Autriche': 2, 'Danemark': 2, 'République d\'Irlande': 2,
  'Pologne': 2, 'Portugal': 2, 'République tchèque': 2,
  // Zone 3
  'Bulgarie': 3, 'Chypre': 3, 'Croatie': 3, 'Estonie': 3,
  'Finlande': 3, 'Grèce': 3, 'Hongrie': 3, 'Lettonie': 3,
  'Lituanie': 3, 'Malte': 3, 'Roumanie': 3, 'Slovaquie': 3,
  'Slovénie': 3, 'Suède': 3,
  // Zone 5
  'Israël': 5, 'Liechtenstein': 5, 'Norvège': 5, 'Suisse': 5, 'Turquie': 5,
  'Jersey': 5, 'Guernesey': 5,
  // Zone 6
  'Canada': 6, 'États-Unis': 6,
  // Zone 7
  'Algérie': 7, 'Chine': 7, 'Hong Kong': 7, 'Inde': 7, 'Corée du Sud': 7,
  'Singapour': 7, 'Taiwan': 7, 'Thaïlande': 7, 'Vietnam': 7, 'Japon': 7,
  'Maroc': 7, 'Tunisie': 7,
  'Guadeloupe': 7, 'Martinique': 7, 'La Réunion': 7, 'Mayotte': 7, 'Guyane': 7,
  // Zone 8
  'Australie': 8, 'Russie': 8, 'Islande': 8,
  'Nouvelle-Calédonie': 8, 'Polynésie Française': 8,
  'Wallis-et-Futuna': 8, 'Terres Australes et Antarctiques Françaises': 8,
  'Saint-Pierre-et-Miquelon': 8, 'Groenland': 8, 'Îles Féroé': 8,
  'Autres pays': 8
};

// DHL Domestic rates (France) - Zone A
const DHL_DOMESTIC_RATES = {
  1: 25.26, 2: 26.68, 3: 28.10, 4: 29.52, 5: 30.94,
  6: 32.36, 7: 33.78, 8: 35.20, 9: 36.62, 10: 38.04,
  11: 40.60, 12: 43.16, 13: 45.72, 14: 48.28, 15: 50.84,
  16: 53.40, 17: 55.96, 18: 58.52, 19: 61.08, 20: 63.64,
  21: 66.96, 22: 70.28, 23: 73.60, 24: 76.92, 25: 80.24,
  26: 83.56, 27: 86.88, 28: 90.20, 29: 93.52, 30: 96.84,
  31: 100.16, 32: 103.48, 33: 106.80, 34: 110.12, 35: 113.44,
  36: 116.76, 37: 120.08, 38: 123.40, 39: 126.72, 40: 130.04,
  41: 133.36, 42: 136.68, 43: 140.00, 44: 143.32, 45: 146.64,
  46: 149.96, 47: 153.28, 48: 156.60, 49: 159.92, 50: 163.24,
  51: 166.56, 52: 169.88, 53: 173.20, 54: 176.52, 55: 179.84,
  56: 183.16, 57: 186.48, 58: 189.80, 59: 193.12, 60: 196.44,
  61: 199.76, 62: 203.08, 63: 206.40, 64: 209.72, 65: 213.04,
  66: 216.36, 67: 219.68, 68: 223.00, 69: 226.32, 70: 229.64
};

// DHL Express Worldwide rates by zone
const DHL_EXPRESS_RATES = {
  1: {
    0.5: 29.03, 1: 36.06, 1.5: 40.99, 2: 45.92, 2.5: 50.85,
    3: 53.24, 3.5: 55.63, 4: 58.02, 4.5: 60.41, 5: 62.80,
    5.5: 64.97, 6: 67.14, 6.5: 69.31, 7: 71.48, 7.5: 73.65,
    8: 75.82, 8.5: 77.99, 9: 80.16, 9.5: 82.33, 10: 84.50,
    10.5: 86.12, 11: 87.74, 11.5: 89.36, 12: 90.98, 12.5: 92.60,
    13: 94.22, 13.5: 95.84, 14: 97.46, 14.5: 99.08, 15: 100.70,
    15.5: 102.32, 16: 103.94, 16.5: 105.56, 17: 107.18, 17.5: 108.80,
    18: 110.42, 18.5: 112.04, 19: 113.66, 19.5: 115.28, 20: 116.90,
    21: 119.66, 22: 122.42, 23: 125.18, 24: 127.94, 25: 130.70,
    26: 133.46, 27: 136.22, 28: 138.98, 29: 141.74, 30: 144.50,
    31: 147.24, 32: 149.98, 33: 152.72, 34: 155.46, 35: 158.20,
    36: 160.94, 37: 163.68, 38: 166.42, 39: 169.16, 40: 171.90,
    41: 174.64, 42: 177.38, 43: 180.12, 44: 182.86, 45: 185.60,
    46: 188.34, 47: 191.08, 48: 193.82, 49: 196.56, 50: 199.30,
    51: 202.04, 52: 204.78, 53: 207.52, 54: 210.26, 55: 213.00,
    56: 215.74, 57: 218.48, 58: 221.22, 59: 223.96, 60: 226.70,
    61: 229.44, 62: 232.18, 63: 234.92, 64: 237.66, 65: 240.40,
    66: 243.14, 67: 245.88, 68: 248.62, 69: 251.36, 70: 254.10
  },
  2: {
    0.5: 33.62, 1: 42.38, 1.5: 48.22, 2: 54.06, 2.5: 59.90,
    3: 62.10, 3.5: 64.30, 4: 66.50, 4.5: 68.70, 5: 70.90,
    5.5: 73.55, 6: 76.20, 6.5: 78.85, 7: 81.50, 7.5: 84.15,
    8: 86.80, 8.5: 89.45, 9: 92.10, 9.5: 94.75, 10: 97.40,
    10.5: 99.55, 11: 101.70, 11.5: 103.85, 12: 106.00, 12.5: 108.15,
    13: 110.30, 13.5: 112.45, 14: 114.60, 14.5: 116.75, 15: 118.90,
    15.5: 121.05, 16: 123.20, 16.5: 125.35, 17: 127.50, 17.5: 129.65,
    18: 131.80, 18.5: 133.95, 19: 136.10, 19.5: 138.25, 20: 140.40,
    21: 144.70, 22: 149.00, 23: 153.30, 24: 157.60, 25: 161.90,
    26: 166.20, 27: 170.50, 28: 174.80, 29: 179.10, 30: 183.40,
    31: 186.85, 32: 190.30, 33: 193.75, 34: 197.20, 35: 200.65,
    36: 204.10, 37: 207.55, 38: 211.00, 39: 214.45, 40: 217.90,
    41: 221.35, 42: 224.80, 43: 228.25, 44: 231.70, 45: 235.15,
    46: 238.60, 47: 242.05, 48: 245.50, 49: 248.95, 50: 252.40,
    51: 255.85, 52: 259.30, 53: 262.75, 54: 266.20, 55: 269.65,
    56: 273.10, 57: 276.55, 58: 280.00, 59: 283.45, 60: 286.90,
    61: 290.35, 62: 293.80, 63: 297.25, 64: 300.70, 65: 304.15,
    66: 307.60, 67: 311.05, 68: 314.50, 69: 317.95, 70: 321.40
  },
  3: {
    0.5: 34.62, 1: 43.64, 1.5: 49.65, 2: 55.66, 2.5: 61.67,
    3: 63.93, 3.5: 66.19, 4: 68.45, 4.5: 70.71, 5: 72.97,
    5.5: 75.51, 6: 78.05, 6.5: 80.59, 7: 83.13, 7.5: 85.67,
    8: 88.21, 8.5: 90.75, 9: 93.29, 9.5: 95.83, 10: 98.37,
    10.5: 100.54, 11: 102.71, 11.5: 104.88, 12: 107.05, 12.5: 109.22,
    13: 111.39, 13.5: 113.56, 14: 115.73, 14.5: 117.90, 15: 120.07,
    15.5: 122.24, 16: 124.41, 16.5: 126.58, 17: 128.75, 17.5: 130.92,
    18: 133.09, 18.5: 135.26, 19: 137.43, 19.5: 139.60, 20: 141.77,
    21: 146.11, 22: 150.45, 23: 154.79, 24: 159.13, 25: 163.47,
    26: 167.81, 27: 172.15, 28: 176.49, 29: 180.83, 30: 185.17,
    31: 190.93, 32: 196.69, 33: 202.45, 34: 208.21, 35: 213.97,
    36: 219.73, 37: 225.49, 38: 231.25, 39: 237.01, 40: 242.77,
    41: 248.53, 42: 254.29, 43: 260.05, 44: 265.81, 45: 271.57,
    46: 277.33, 47: 283.09, 48: 288.85, 49: 294.61, 50: 300.37,
    51: 306.13, 52: 311.89, 53: 317.65, 54: 323.41, 55: 329.17,
    56: 334.93, 57: 340.69, 58: 346.45, 59: 352.21, 60: 357.97,
    61: 363.73, 62: 369.49, 63: 375.25, 64: 381.01, 65: 386.77,
    66: 392.53, 67: 398.29, 68: 404.05, 69: 409.81, 70: 415.57
  },
  4: {
    0.5: 31.08, 1: 41.30, 1.5: 45.88, 2: 50.46, 2.5: 62.46,
    3: 64.79, 3.5: 67.12, 4: 69.45, 4.5: 71.78, 5: 74.11,
    5.5: 76.33, 6: 78.55, 6.5: 80.77, 7: 82.99, 7.5: 85.21,
    8: 87.43, 8.5: 89.65, 9: 91.87, 9.5: 94.09, 10: 96.31,
    10.5: 98.10, 11: 99.89, 11.5: 101.68, 12: 103.47, 12.5: 105.26,
    13: 107.05, 13.5: 108.84, 14: 110.63, 14.5: 112.42, 15: 114.21,
    15.5: 116.00, 16: 117.79, 16.5: 119.58, 17: 121.37, 17.5: 123.16,
    18: 124.95, 18.5: 126.74, 19: 128.53, 19.5: 130.32, 20: 132.11,
    21: 135.65, 22: 139.19, 23: 142.73, 24: 146.27, 25: 149.81,
    26: 153.35, 27: 156.89, 28: 160.43, 29: 163.97, 30: 167.51,
    31: 171.01, 32: 174.51, 33: 178.01, 34: 181.51, 35: 185.01,
    36: 188.51, 37: 192.01, 38: 195.51, 39: 199.01, 40: 202.51,
    41: 206.01, 42: 209.51, 43: 213.01, 44: 216.51, 45: 220.01,
    46: 223.51, 47: 227.01, 48: 230.51, 49: 234.01, 50: 237.51,
    51: 241.01, 52: 244.51, 53: 248.01, 54: 251.51, 55: 255.01,
    56: 258.51, 57: 262.01, 58: 265.51, 59: 269.01, 60: 272.51,
    61: 276.01, 62: 279.51, 63: 283.01, 64: 286.51, 65: 290.01,
    66: 293.51, 67: 297.01, 68: 300.51, 69: 304.01, 70: 307.51
  },
  5: {
    0.5: 31.09, 1: 44.50, 1.5: 52.50, 2: 60.50, 2.5: 72.38,
    3: 75.05, 3.5: 77.72, 4: 80.39, 4.5: 83.06, 5: 85.73,
    5.5: 88.17, 6: 90.61, 6.5: 93.05, 7: 95.49, 7.5: 97.93,
    8: 100.37, 8.5: 102.81, 9: 105.25, 9.5: 107.69, 10: 110.13,
    10.5: 112.80, 11: 115.47, 11.5: 118.14, 12: 120.81, 12.5: 123.48,
    13: 126.15, 13.5: 128.82, 14: 131.49, 14.5: 134.16, 15: 136.83,
    15.5: 139.50, 16: 142.17, 16.5: 144.84, 17: 147.51, 17.5: 150.18,
    18: 152.85, 18.5: 155.52, 19: 158.19, 19.5: 160.86, 20: 163.53,
    21: 169.63, 22: 175.73, 23: 181.83, 24: 187.93, 25: 194.03,
    26: 200.13, 27: 206.23, 28: 212.33, 29: 218.43, 30: 224.53,
    31: 233.05, 32: 241.57, 33: 250.09, 34: 258.61, 35: 267.13,
    36: 275.65, 37: 284.17, 38: 292.69, 39: 301.21, 40: 309.73,
    41: 318.25, 42: 326.77, 43: 335.29, 44: 343.81, 45: 352.33,
    46: 360.85, 47: 369.37, 48: 377.89, 49: 386.41, 50: 394.93,
    51: 403.45, 52: 411.97, 53: 420.49, 54: 429.01, 55: 437.53,
    56: 446.05, 57: 454.57, 58: 463.09, 59: 471.61, 60: 480.13,
    61: 488.65, 62: 497.17, 63: 505.69, 64: 514.21, 65: 522.73,
    66: 531.25, 67: 539.77, 68: 548.29, 69: 556.81, 70: 565.33
  },
  6: {
    0.5: 31.73, 1: 47.19, 1.5: 56.06, 2: 64.93, 2.5: 73.11,
    3: 75.90, 3.5: 78.69, 4: 81.48, 4.5: 84.27, 5: 87.06,
    5.5: 89.51, 6: 91.96, 6.5: 94.41, 7: 96.86, 7.5: 99.31,
    8: 101.76, 8.5: 104.21, 9: 106.66, 9.5: 109.11, 10: 111.56,
    10.5: 114.87, 11: 118.18, 11.5: 121.49, 12: 124.80, 12.5: 128.11,
    13: 131.42, 13.5: 134.73, 14: 138.04, 14.5: 141.35, 15: 144.66,
    15.5: 147.97, 16: 151.28, 16.5: 154.59, 17: 157.90, 17.5: 161.21,
    18: 164.52, 18.5: 167.83, 19: 171.14, 19.5: 174.45, 20: 177.76,
    21: 186.00, 22: 194.24, 23: 202.48, 24: 210.72, 25: 218.96,
    26: 227.20, 27: 235.44, 28: 243.68, 29: 251.92, 30: 260.16,
    31: 269.06, 32: 277.96, 33: 286.86, 34: 295.76, 35: 304.66,
    36: 313.56, 37: 322.46, 38: 331.36, 39: 340.26, 40: 349.16,
    41: 358.06, 42: 366.96, 43: 375.86, 44: 384.76, 45: 393.66,
    46: 402.56, 47: 411.46, 48: 420.36, 49: 429.26, 50: 438.16,
    51: 447.06, 52: 455.96, 53: 464.86, 54: 473.76, 55: 482.66,
    56: 491.56, 57: 500.46, 58: 509.36, 59: 518.26, 60: 527.16,
    61: 536.06, 62: 544.96, 63: 553.86, 64: 562.76, 65: 571.66,
    66: 580.56, 67: 589.46, 68: 598.36, 69: 607.26, 70: 616.16
  },
  7: {
    0.5: 50.91, 1: 65.31, 1.5: 74.10, 2: 82.89, 2.5: 91.68,
    3: 98.55, 3.5: 105.42, 4: 112.29, 4.5: 119.16, 5: 126.03,
    5.5: 132.38, 6: 138.73, 6.5: 145.08, 7: 151.43, 7.5: 157.78,
    8: 164.13, 8.5: 170.48, 9: 176.83, 9.5: 183.18, 10: 189.53,
    10.5: 196.68, 11: 203.83, 11.5: 210.98, 12: 218.13, 12.5: 225.28,
    13: 232.43, 13.5: 239.58, 14: 246.73, 14.5: 253.88, 15: 261.03,
    15.5: 268.18, 16: 275.33, 16.5: 282.48, 17: 289.63, 17.5: 296.78,
    18: 303.93, 18.5: 311.08, 19: 318.23, 19.5: 325.38, 20: 332.53,
    21: 346.99, 22: 361.45, 23: 375.91, 24: 390.37, 25: 404.83,
    26: 419.29, 27: 433.75, 28: 448.21, 29: 462.67, 30: 477.13,
    31: 492.51, 32: 507.89, 33: 523.27, 34: 538.65, 35: 554.03,
    36: 569.41, 37: 584.79, 38: 600.17, 39: 615.55, 40: 630.93,
    41: 646.31, 42: 661.69, 43: 677.07, 44: 692.45, 45: 707.83,
    46: 723.21, 47: 738.59, 48: 753.97, 49: 769.35, 50: 784.73,
    51: 800.11, 52: 815.49, 53: 830.87, 54: 846.25, 55: 861.63,
    56: 877.01, 57: 892.39, 58: 907.77, 59: 923.15, 60: 938.53,
    61: 953.91, 62: 969.29, 63: 984.67, 64: 1000.05, 65: 1015.43,
    66: 1030.81, 67: 1046.19, 68: 1061.57, 69: 1076.95, 70: 1092.33
  },
  8: {
    0.5: 67.12, 1: 87.27, 1.5: 99.03, 2: 110.79, 2.5: 122.55,
    3: 131.37, 3.5: 140.19, 4: 149.01, 4.5: 157.83, 5: 166.65,
    5.5: 176.04, 6: 185.43, 6.5: 194.82, 7: 204.21, 7.5: 213.60,
    8: 222.99, 8.5: 232.38, 9: 241.77, 9.5: 251.16, 10: 260.55,
    10.5: 270.75, 11: 280.95, 11.5: 291.15, 12: 301.35, 12.5: 311.55,
    13: 321.75, 13.5: 331.95, 14: 342.15, 14.5: 352.35, 15: 362.55,
    15.5: 372.75, 16: 382.95, 16.5: 393.15, 17: 403.35, 17.5: 413.55,
    18: 423.75, 18.5: 433.95, 19: 444.15, 19.5: 454.35, 20: 464.55,
    21: 484.95, 22: 505.35, 23: 525.75, 24: 546.15, 25: 566.55,
    26: 586.95, 27: 607.35, 28: 627.75, 29: 648.15, 30: 668.55,
    31: 688.21, 32: 707.87, 33: 727.53, 34: 747.19, 35: 766.85,
    36: 786.51, 37: 806.17, 38: 825.83, 39: 845.49, 40: 865.15,
    41: 884.81, 42: 904.47, 43: 924.13, 44: 943.79, 45: 963.45,
    46: 983.11, 47: 1002.77, 48: 1022.43, 49: 1042.09, 50: 1061.75,
    51: 1081.41, 52: 1101.07, 53: 1120.73, 54: 1140.39, 55: 1160.05,
    56: 1179.71, 57: 1199.37, 58: 1219.03, 59: 1238.69, 60: 1258.35,
    61: 1278.01, 62: 1297.67, 63: 1317.33, 64: 1336.99, 65: 1356.65,
    66: 1376.31, 67: 1395.97, 68: 1415.63, 69: 1435.29, 70: 1454.95
  }
};

// MECHANIZABLE LIMITS (for box dimensions)
const MECHANIZABLE_LIMITS = {
  minLength: 14, maxLength: 100,
  minWidth: 9, maxWidth: 60,
  minHeight: 1, maxHeight: 30,
  maxWeight: 30
};

// COLISSIMO LIMITS (as per carrier specifications)
const COLISSIMO_LIMITS = {
  maxWeight: 30, // kg
  maxSumDimensions: 150, // L + W + H in cm
  maxLongestSide: 100 // cm
};

// ========================================
// DB SCHENKER SHIPPING RATES & ZONES
// ========================================

// Schenker France Zones (by department prefix)
// Zone 1: 04-06-13-20-83-84 (PACA + Corse)
// Zone 2: 05-07-11-12-15-26-30-34-38-42-43-48-66-69-73-81 (South-East/Mid)
// Zone 3: Most of France
// Zone 4: 02-08-14-22-27-29-35-50-51-54-55-56-57-59-60-61-62-67-68-76-80-88 (North/East)

const SCHENKER_FRANCE_ZONE_1 = ['04', '06', '13', '20', '83', '84'];
const SCHENKER_FRANCE_ZONE_2 = ['05', '07', '11', '12', '15', '26', '30', '34', '38', '42', '43', '48', '66', '69', '73', '81'];
const SCHENKER_FRANCE_ZONE_3 = ['01', '03', '09', '10', '16', '17', '18', '19', '21', '23', '24', '25', '28', '31', '32', '33', '36', '37', '39', '40', '41', '44', '45', '46', '47', '49', '52', '53', '58', '63', '64', '65', '70', '71', '72', '74', '75', '77', '78', '79', '82', '85', '86', '87', '89', '90', '91', '92', '93', '94', '95'];
const SCHENKER_FRANCE_ZONE_4 = ['02', '08', '14', '22', '27', '29', '35', '50', '51', '54', '55', '56', '57', '59', '60', '61', '62', '67', '68', '76', '80', '88'];

// Schenker France Rates (per shipment, EUR HT)
// Up to weight brackets in kg
const SCHENKER_FRANCE_RATES = {
  1: { 9: 18.13, 19: 21.54, 29: 24.64, 39: 29.29, 49: 31.46, 59: 34.09, 69: 36.26, 79: 38.43, 100: 45.26 },
  2: { 9: 26.35, 19: 31.29, 29: 35.65, 39: 42.62, 49: 45.70, 59: 49.44, 69: 52.54, 79: 55.80, 100: 65.70 },
  3: { 9: 34.26, 19: 40.75, 29: 46.50, 39: 55.33, 49: 59.50, 59: 64.31, 69: 68.35, 79: 72.49, 100: 85.55 },
  4: { 9: 38.59, 19: 45.86, 29: 52.38, 39: 62.43, 49: 67.12, 59: 72.49, 69: 77.17, 79: 81.67, 100: 96.39 }
};

// Schenker France per 100kg rates (for heavier shipments)
const SCHENKER_FRANCE_PER_100KG = {
  1: { 299: 43.08, 499: 43.08, 699: 38.74, 999: 38.74, 3000: 32.38 },
  2: { 299: 62.59, 499: 62.59, 699: 56.41, 999: 56.41, 3000: 46.95 },
  3: { 299: 81.37, 499: 81.37, 699: 73.32, 999: 73.32, 3000: 61.06 },
  4: { 299: 91.90, 499: 91.90, 699: 82.61, 999: 82.61, 3000: 68.81 }
};

// Schenker Europe Rates (per shipment, EUR HT) - simplified for main countries
// Format: { weightBracket: price }
const SCHENKER_EUROPE_RATES = {
  'Allemagne': { 50: 75.12, 100: 86.59, 150: 108.52, 200: 135.13, 250: 159.52, 300: 186.63, 350: 210.96, 400: 238.00, 450: 262.39, 500: 289.44, per100kg: 51.85 },
  'Belgique': { 50: 70.85, 100: 80.34, 150: 104.71, 200: 129.22, 250: 151.04, 300: 175.62, 350: 197.47, 400: 221.97, 450: 243.86, 500: 268.37, per100kg: 46.06 },
  'Pays-Bas': { 50: 74.65, 100: 86.23, 150: 111.56, 200: 138.65, 250: 163.01, 300: 190.13, 350: 214.50, 400: 241.52, 450: 265.95, 500: 293.04, per100kg: 51.82 },
  'Luxembourg': { 50: 66.34, 100: 99.54, 150: 127.44, 200: 178.42, 250: 229.40, 300: 280.37, 350: 331.32, 400: 382.30, 450: 433.28, 500: 484.25, per100kg: 61.70 },
  'Italie': { 50: 61.11, 100: 70.15, 150: 90.64, 200: 110.37, 250: 127.56, 300: 147.33, 350: 164.54, 400: 184.27, 450: 201.50, 500: 221.24, per100kg: 36.00 },
  'Espagne': { 50: 72.51, 100: 84.74, 150: 107.92, 200: 133.75, 250: 156.47, 300: 182.26, 350: 205.06, 400: 230.79, 450: 253.60, 500: 279.41, per100kg: 48.50 },
  'Portugal': { 50: 87.54, 100: 96.81, 150: 124.95, 200: 157.32, 250: 185.26, 300: 217.58, 350: 245.65, 400: 277.90, 450: 305.97, 500: 338.30, per100kg: 60.20 },
  'Autriche': { 50: 89.97, 100: 103.22, 150: 130.55, 200: 163.25, 250: 193.71, 300: 226.81, 350: 257.26, 400: 290.34, 450: 320.78, 500: 353.90, per100kg: 64.90 },
  'Suisse': { 50: 86.44, 100: 98.66, 150: 128.37, 200: 156.46, 250: 181.70, 300: 209.84, 350: 235.10, 400: 263.16, 450: 288.48, 500: 316.58, per100kg: 52.89 },
  'Royaume-Uni': { 50: 108.85, 100: 122.44, 150: 159.33, 200: 200.37, 250: 235.97, 300: 277.49, 350: 313.39, 400: 354.88, 450: 390.78, 500: 432.33, per100kg: 61.37 },
  'Danemark': { 50: 112.33, 100: 125.60, 150: 137.02, 200: 170.88, 250: 202.03, 300: 235.89, 350: 267.04, 400: 300.84, 450: 332.05, 500: 365.87, per100kg: 65.54 },
  'Suède': { 50: 116.04, 100: 130.11, 150: 139.07, 200: 178.90, 250: 216.04, 300: 255.82, 350: 292.96, 400: 332.72, 450: 369.88, 500: 409.71, per100kg: 79.28 },
  'Pologne': { 50: 72.66, 100: 80.47, 150: 104.84, 200: 132.95, 250: 158.37, 300: 186.48, 350: 211.92, 400: 239.99, 450: 265.49, 500: 293.59, per100kg: 54.07 },
  'République tchèque': { 50: 79.97, 100: 91.18, 150: 118.44, 200: 148.98, 250: 176.83, 300: 207.38, 350: 235.22, 400: 265.69, 450: 293.59, 500: 324.13, per100kg: 59.33 },
  'Hongrie': { 50: 79.76, 100: 89.52, 150: 116.52, 200: 148.58, 250: 177.99, 300: 210.10, 350: 239.46, 400: 271.50, 450: 300.94, 500: 333.01, per100kg: 62.82 },
  'Slovaquie': { 50: 110.73, 100: 120.92, 150: 131.30, 200: 167.41, 250: 198.30, 300: 234.44, 350: 265.31, 400: 301.38, 450: 332.33, 500: 368.43, per100kg: 67.26 },
  'Slovénie': { 50: 125.27, 100: 138.51, 150: 154.72, 200: 191.59, 250: 227.79, 300: 269.57, 350: 305.78, 400: 347.52, 450: 383.77, 500: 425.61, per100kg: 79.86 },
  'Croatie': { 50: 148.69, 100: 167.02, 150: 198.00, 200: 250.87, 250: 298.37, 300: 352.30, 350: 390.42, 400: 443.43, 450: 489.77, 500: 542.67, per100kg: 92.57 },
  'Grèce': { 50: 136.39, 100: 142.78, 150: 161.89, 200: 203.38, 250: 249.49, 300: 291.06, 350: 340.90, 400: 382.37, 450: 430.45, 500: 471.99, per100kg: 96.67 },
  'Roumanie': { 50: 120.88, 100: 130.35, 150: 177.60, 200: 228.43, 250: 273.08, 300: 323.95, 350: 368.58, 400: 419.38, 450: 464.06, 500: 514.91, per100kg: 96.92 },
  'Bulgarie': { 50: 154.09, 100: 166.93, 150: 204.61, 200: 264.40, 250: 320.06, 300: 379.91, 350: 383.28, 400: 396.27, 450: 440.83, 500: 488.69, per100kg: 80.50 },
  'Finlande': { 50: 113.77, 100: 124.60, 150: 136.13, 200: 174.44, 250: 209.96, 300: 248.21, 350: 283.84, 400: 322.01, 450: 357.61, 500: 395.91, per100kg: 75.30 },
  'Estonie': { 50: 116.51, 100: 128.62, 150: 155.27, 200: 200.13, 250: 239.08, 300: 283.95, 350: 322.96, 400: 367.70, 450: 406.77, 500: 451.57, per100kg: 84.82 },
  'Lettonie': { 50: 118.80, 100: 131.93, 150: 160.25, 200: 205.07, 250: 244.11, 300: 289.01, 350: 328.00, 400: 372.79, 450: 411.90, 500: 456.76, per100kg: 85.11 },
  'Lituanie': { 50: 118.61, 100: 132.22, 150: 159.96, 200: 203.58, 250: 241.40, 300: 285.07, 350: 322.88, 400: 366.46, 450: 404.33, 500: 447.96, per100kg: 82.28 },
  'République d\'Irlande': { 50: 134.15, 100: 148.58, 150: 197.21, 200: 252.02, 250: 299.16, 300: 353.95, 350: 401.21, 400: 455.98, 450: 503.24, 500: 558.03, per100kg: 81.10 },
  'Norvège': { 50: 124.19, 100: 136.15, 150: 149.06, 200: 190.27, 250: 228.85, 300: 270.05, 350: 308.65, 400: 349.86, 450: 388.40, 500: 429.65, per100kg: 81.42 }
};

// Schenker additional fees
const SCHENKER_FEES = {
  CONTRIBUTION_SURETE: 1.45, // per shipment (domestic)
  CONTRIBUTION_SURETE_INTL: 4.19, // per shipment (international)
  CONTRIBUTION_ENERGIE: 1.05, // per shipment
  LIVRAISON_PARTICULIER: 5.27, // delivery to private individual
  REGION_PARISIENNE: 9.81, // Paris region surcharge (75-77-78-91-92-93-94-95)
  CORSE_SURCHARGE: 0.80, // 80% surcharge for Corsica
  ILES_ALTITUDE_SURCHARGE: 0.50 // 50% surcharge for islands and altitude locations
};

// Schenker limits
const SCHENKER_LIMITS = {
  maxWeightPerPackage: 30, // kg for vrac (loose) packages
  maxWeightPerShipment: 2500, // kg
  maxLength: 240, // cm for packages > 30kg
  maxWidth: 120, // cm
  maxHeight: 220, // cm
  minDensity: 50 // kg/m³ (national)
};

// CUSTOM FRAME SIZES - Client's lookup table
// Format: 'width×height': 'outerWidth×outerHeight'
const CUSTOM_FRAME_SIZES = {
  // Square artworks
  '35×35': '45×45',
  '50×50': '64×64',
  '60×60': '78×78',
  '70×70': '91×91',
  '80×80': '104×104',
  '90×90': '116×116',
  '105×105': '135×135',
  '120×120': '154×154',
  '133×133': '172×172',

  // Portrait artworks (and their landscape equivalents)
  '35×70': '45×91',
  '70×35': '91×45',
  '50×100': '64×114',
  '100×50': '114×64',
  '60×120': '77×137',
  '120×60': '137×77',
  '70×140': '91×161',
  '140×70': '161×91',
  '77×154': '100×177',
  '154×77': '177×100',
  '90×180': '116×206',
  '180×90': '206×116',
  '105×210': '135×240',
  '210×105': '240×135',
  '120×240': '154×274',
  '240×120': '274×154',
  '133×266': '172×306',
  '266×133': '306×172',

  // Other dimensions
  '50×68': '64×74',
  '68×50': '74×64',
  '60×82': '77×99',
  '82×60': '99×77',
  '70×96': '91×117',
  '96×70': '117×91',
  '80×96': '104×120',
  '96×80': '120×104',
  '80×110': '104×134',
  '110×80': '134×104',
  '80×115': '104×139',
  '115×80': '139×104',
  '90×108': '116×134',
  '108×90': '134×116',
  '90×124': '116×150',
  '124×90': '150×116',
  '105×126': '135×156',
  '126×105': '156×135',
  '120×144': '154×178',
  '144×120': '178×154'
};

// POPULAR FRAME COLORS - Premium selection
const POPULAR_COLORS = [
  { name: 'Classic Black', hex: '#1A1A1A', ral: 'RAL 9005' },
  { name: 'Pure White', hex: '#F5F5F5', ral: 'RAL 9010' },
  { name: 'Champagne Gold', hex: '#D4AF37', ral: 'RAL 1036' },
  { name: 'Natural Wood', hex: '#8B7355', ral: 'RAL 8024' },
  { name: 'Platinum Silver', hex: '#C0C0C0', ral: 'RAL 9006' },
  { name: 'Charcoal Grey', hex: '#4A4A4A', ral: 'RAL 7024' },
  { name: 'Navy Blue', hex: '#001F3F', ral: 'RAL 5003' },
  { name: 'Deep Red', hex: '#8B0000', ral: 'RAL 3011' }
];

// RAL COLOR CODES - Complete RAL Classic collection
const RAL_COLORS = [
  // RAL 1xxx - Yellow/Beige
  { code: 'RAL 1000', name: 'Green Beige', hex: '#BEBD7F' },
  { code: 'RAL 1001', name: 'Beige', hex: '#C2B078' },
  { code: 'RAL 1002', name: 'Sand Yellow', hex: '#C6A664' },
  { code: 'RAL 1003', name: 'Signal Yellow', hex: '#E5BE01' },
  { code: 'RAL 1004', name: 'Golden Yellow', hex: '#CDA434' },
  { code: 'RAL 1005', name: 'Honey Yellow', hex: '#A98307' },
  { code: 'RAL 1006', name: 'Maize Yellow', hex: '#E4A010' },
  { code: 'RAL 1007', name: 'Daffodil Yellow', hex: '#DC9D00' },
  { code: 'RAL 1011', name: 'Brown Beige', hex: '#8A6642' },
  { code: 'RAL 1012', name: 'Lemon Yellow', hex: '#C7B446' },
  { code: 'RAL 1013', name: 'Oyster White', hex: '#EAE6CA' },
  { code: 'RAL 1014', name: 'Ivory', hex: '#E1CC4F' },
  { code: 'RAL 1015', name: 'Light Ivory', hex: '#E6D690' },
  { code: 'RAL 1016', name: 'Sulfur Yellow', hex: '#EDFF21' },
  { code: 'RAL 1017', name: 'Saffron Yellow', hex: '#F5D033' },
  { code: 'RAL 1018', name: 'Zinc Yellow', hex: '#F8F32B' },
  { code: 'RAL 1019', name: 'Grey Beige', hex: '#9E9764' },
  { code: 'RAL 1020', name: 'Olive Yellow', hex: '#999950' },
  { code: 'RAL 1021', name: 'Rape Yellow', hex: '#F3DA0B' },
  { code: 'RAL 1023', name: 'Traffic Yellow', hex: '#FAD201' },
  { code: 'RAL 1024', name: 'Ochre Yellow', hex: '#AEA04B' },
  { code: 'RAL 1027', name: 'Curry', hex: '#9D9101' },
  { code: 'RAL 1028', name: 'Melon Yellow', hex: '#F4A900' },
  { code: 'RAL 1032', name: 'Broom Yellow', hex: '#D6AE01' },
  { code: 'RAL 1033', name: 'Dahlia Yellow', hex: '#F3A505' },
  { code: 'RAL 1034', name: 'Pastel Yellow', hex: '#EFA94A' },
  { code: 'RAL 1036', name: 'Pearl Gold', hex: '#927549' },
  { code: 'RAL 1037', name: 'Sun Yellow', hex: '#F39F18' },

  // RAL 2xxx - Orange
  { code: 'RAL 2000', name: 'Yellow Orange', hex: '#ED760E' },
  { code: 'RAL 2001', name: 'Red Orange', hex: '#C93C20' },
  { code: 'RAL 2002', name: 'Vermilion', hex: '#CB2821' },
  { code: 'RAL 2003', name: 'Pastel Orange', hex: '#FF7514' },
  { code: 'RAL 2004', name: 'Pure Orange', hex: '#F44611' },
  { code: 'RAL 2005', name: 'Luminous Orange', hex: '#FF2301' },
  { code: 'RAL 2007', name: 'Luminous Bright Orange', hex: '#FFA420' },
  { code: 'RAL 2008', name: 'Bright Red Orange', hex: '#F75E25' },
  { code: 'RAL 2009', name: 'Traffic Orange', hex: '#F54021' },
  { code: 'RAL 2010', name: 'Signal Orange', hex: '#D84B20' },
  { code: 'RAL 2011', name: 'Deep Orange', hex: '#EC7C26' },
  { code: 'RAL 2012', name: 'Salmon Orange', hex: '#E55137' },
  { code: 'RAL 2013', name: 'Pearl Orange', hex: '#C35831' },

  // RAL 3xxx - Red
  { code: 'RAL 3000', name: 'Flame Red', hex: '#AF2B1E' },
  { code: 'RAL 3001', name: 'Signal Red', hex: '#A52019' },
  { code: 'RAL 3002', name: 'Carmine Red', hex: '#A2231D' },
  { code: 'RAL 3003', name: 'Ruby Red', hex: '#9B111E' },
  { code: 'RAL 3004', name: 'Purple Red', hex: '#75151E' },
  { code: 'RAL 3005', name: 'Wine Red', hex: '#5E2129' },
  { code: 'RAL 3007', name: 'Black Red', hex: '#412227' },
  { code: 'RAL 3009', name: 'Oxide Red', hex: '#642424' },
  { code: 'RAL 3011', name: 'Brown Red', hex: '#792423' },
  { code: 'RAL 3012', name: 'Beige Red', hex: '#C1876B' },
  { code: 'RAL 3013', name: 'Tomato Red', hex: '#A12312' },
  { code: 'RAL 3014', name: 'Antique Pink', hex: '#D36E70' },
  { code: 'RAL 3015', name: 'Light Pink', hex: '#EA899A' },
  { code: 'RAL 3016', name: 'Coral Red', hex: '#B32821' },
  { code: 'RAL 3017', name: 'Rose', hex: '#E63244' },
  { code: 'RAL 3018', name: 'Strawberry Red', hex: '#D53032' },
  { code: 'RAL 3020', name: 'Traffic Red', hex: '#CC0605' },
  { code: 'RAL 3022', name: 'Salmon Pink', hex: '#D95030' },
  { code: 'RAL 3027', name: 'Raspberry Red', hex: '#C51D34' },
  { code: 'RAL 3031', name: 'Orient Red', hex: '#B32428' },
  { code: 'RAL 3032', name: 'Pearl Ruby Red', hex: '#721422' },
  { code: 'RAL 3033', name: 'Pearl Pink', hex: '#B44C43' },

  // RAL 4xxx - Violet
  { code: 'RAL 4001', name: 'Red Lilac', hex: '#6D3F5B' },
  { code: 'RAL 4002', name: 'Red Violet', hex: '#922B3E' },
  { code: 'RAL 4003', name: 'Heather Violet', hex: '#DE4C8A' },
  { code: 'RAL 4004', name: 'Claret Violet', hex: '#641C34' },
  { code: 'RAL 4005', name: 'Blue Lilac', hex: '#6C4675' },
  { code: 'RAL 4006', name: 'Traffic Purple', hex: '#A03472' },
  { code: 'RAL 4007', name: 'Purple Violet', hex: '#4A192C' },
  { code: 'RAL 4008', name: 'Signal Violet', hex: '#924E7D' },
  { code: 'RAL 4009', name: 'Pastel Violet', hex: '#A18594' },
  { code: 'RAL 4010', name: 'Telemagenta', hex: '#CF3476' },
  { code: 'RAL 4011', name: 'Pearl Violet', hex: '#8673A1' },
  { code: 'RAL 4012', name: 'Pearl Blackberry', hex: '#6C6874' },

  // RAL 5xxx - Blue
  { code: 'RAL 5000', name: 'Violet Blue', hex: '#354D73' },
  { code: 'RAL 5001', name: 'Green Blue', hex: '#1F3438' },
  { code: 'RAL 5002', name: 'Ultramarine Blue', hex: '#20214F' },
  { code: 'RAL 5003', name: 'Sapphire Blue', hex: '#1D1E33' },
  { code: 'RAL 5004', name: 'Black Blue', hex: '#18171C' },
  { code: 'RAL 5005', name: 'Signal Blue', hex: '#1E2460' },
  { code: 'RAL 5007', name: 'Brillant Blue', hex: '#3E5F8A' },
  { code: 'RAL 5008', name: 'Grey Blue', hex: '#26252D' },
  { code: 'RAL 5009', name: 'Azure Blue', hex: '#025669' },
  { code: 'RAL 5010', name: 'Gentian Blue', hex: '#0E4379' },
  { code: 'RAL 5011', name: 'Steel Blue', hex: '#231A24' },
  { code: 'RAL 5012', name: 'Light Blue', hex: '#3B83BD' },
  { code: 'RAL 5013', name: 'Cobalt Blue', hex: '#1E213D' },
  { code: 'RAL 5014', name: 'Pigeon Blue', hex: '#606E8C' },
  { code: 'RAL 5015', name: 'Sky Blue', hex: '#2271B3' },
  { code: 'RAL 5017', name: 'Traffic Blue', hex: '#063971' },
  { code: 'RAL 5018', name: 'Turquoise Blue', hex: '#3F888F' },
  { code: 'RAL 5019', name: 'Capri Blue', hex: '#1B5583' },
  { code: 'RAL 5020', name: 'Ocean Blue', hex: '#1D334A' },
  { code: 'RAL 5021', name: 'Water Blue', hex: '#256D7B' },
  { code: 'RAL 5022', name: 'Night Blue', hex: '#252850' },
  { code: 'RAL 5023', name: 'Distant Blue', hex: '#49678D' },
  { code: 'RAL 5024', name: 'Pastel Blue', hex: '#5D9B9B' },
  { code: 'RAL 5025', name: 'Pearl Gentian Blue', hex: '#2A6478' },
  { code: 'RAL 5026', name: 'Pearl Night Blue', hex: '#102C54' },

  // RAL 6xxx - Green
  { code: 'RAL 6000', name: 'Patina Green', hex: '#316650' },
  { code: 'RAL 6001', name: 'Emerald Green', hex: '#287233' },
  { code: 'RAL 6002', name: 'Leaf Green', hex: '#2D572C' },
  { code: 'RAL 6003', name: 'Olive Green', hex: '#424632' },
  { code: 'RAL 6004', name: 'Blue Green', hex: '#1F3A3D' },
  { code: 'RAL 6005', name: 'Moss Green', hex: '#2F4F4F' },
  { code: 'RAL 6006', name: 'Grey Olive', hex: '#3E3B32' },
  { code: 'RAL 6007', name: 'Bottle Green', hex: '#343B29' },
  { code: 'RAL 6008', name: 'Brown Green', hex: '#39352A' },
  { code: 'RAL 6009', name: 'Fir Green', hex: '#31372B' },
  { code: 'RAL 6010', name: 'Grass Green', hex: '#35682D' },
  { code: 'RAL 6011', name: 'Reseda Green', hex: '#587246' },
  { code: 'RAL 6012', name: 'Black Green', hex: '#343E40' },
  { code: 'RAL 6013', name: 'Reed Green', hex: '#6C7156' },
  { code: 'RAL 6014', name: 'Yellow Olive', hex: '#47402E' },
  { code: 'RAL 6015', name: 'Black Olive', hex: '#3B3C36' },
  { code: 'RAL 6016', name: 'Turquoise Green', hex: '#1E5945' },
  { code: 'RAL 6017', name: 'May Green', hex: '#4C9141' },
  { code: 'RAL 6018', name: 'Yellow Green', hex: '#57A639' },
  { code: 'RAL 6019', name: 'Pastel Green', hex: '#BDECB6' },
  { code: 'RAL 6020', name: 'Chrome Green', hex: '#2E3A23' },
  { code: 'RAL 6021', name: 'Pale Green', hex: '#89AC76' },
  { code: 'RAL 6022', name: 'Olive Drab', hex: '#25221B' },
  { code: 'RAL 6024', name: 'Traffic Green', hex: '#308446' },
  { code: 'RAL 6025', name: 'Fern Green', hex: '#3D642D' },
  { code: 'RAL 6026', name: 'Opal Green', hex: '#015D52' },
  { code: 'RAL 6027', name: 'Light Green', hex: '#84C3BE' },
  { code: 'RAL 6028', name: 'Pine Green', hex: '#2C5545' },
  { code: 'RAL 6029', name: 'Mint Green', hex: '#20603D' },
  { code: 'RAL 6032', name: 'Signal Green', hex: '#317F43' },
  { code: 'RAL 6033', name: 'Mint Turquoise', hex: '#497E76' },
  { code: 'RAL 6034', name: 'Pastel Turquoise', hex: '#7FB5B5' },
  { code: 'RAL 6035', name: 'Pearl Green', hex: '#1C542D' },
  { code: 'RAL 6036', name: 'Pearl Opal Green', hex: '#193737' },
  { code: 'RAL 6037', name: 'Pure Green', hex: '#008F39' },
  { code: 'RAL 6038', name: 'Luminous Green', hex: '#00BB2D' },

  // RAL 7xxx - Grey
  { code: 'RAL 7000', name: 'Squirrel Grey', hex: '#78858B' },
  { code: 'RAL 7001', name: 'Silver Grey', hex: '#8A9597' },
  { code: 'RAL 7002', name: 'Olive Grey', hex: '#7E7B52' },
  { code: 'RAL 7003', name: 'Moss Grey', hex: '#6C7059' },
  { code: 'RAL 7004', name: 'Signal Grey', hex: '#969992' },
  { code: 'RAL 7005', name: 'Mouse Grey', hex: '#646B63' },
  { code: 'RAL 7006', name: 'Beige Grey', hex: '#6D6552' },
  { code: 'RAL 7008', name: 'Khaki Grey', hex: '#6A5F31' },
  { code: 'RAL 7009', name: 'Green Grey', hex: '#4D5645' },
  { code: 'RAL 7010', name: 'Tarpaulin Grey', hex: '#4C514A' },
  { code: 'RAL 7011', name: 'Iron Grey', hex: '#434B4D' },
  { code: 'RAL 7012', name: 'Basalt Grey', hex: '#4E5754' },
  { code: 'RAL 7013', name: 'Brown Grey', hex: '#464531' },
  { code: 'RAL 7015', name: 'Slate Grey', hex: '#434750' },
  { code: 'RAL 7016', name: 'Anthracite Grey', hex: '#293133' },
  { code: 'RAL 7021', name: 'Black Grey', hex: '#23282B' },
  { code: 'RAL 7022', name: 'Umbra Grey', hex: '#332F2C' },
  { code: 'RAL 7023', name: 'Concrete Grey', hex: '#686C5E' },
  { code: 'RAL 7024', name: 'Graphite Grey', hex: '#474A51' },
  { code: 'RAL 7026', name: 'Granite Grey', hex: '#2F353B' },
  { code: 'RAL 7030', name: 'Stone Grey', hex: '#8B8C7A' },
  { code: 'RAL 7031', name: 'Blue Grey', hex: '#474B4E' },
  { code: 'RAL 7032', name: 'Pebble Grey', hex: '#B8B799' },
  { code: 'RAL 7033', name: 'Cement Grey', hex: '#7D8471' },
  { code: 'RAL 7034', name: 'Yellow Grey', hex: '#8F8B66' },
  { code: 'RAL 7035', name: 'Light Grey', hex: '#D7D7D7' },
  { code: 'RAL 7036', name: 'Platinum Grey', hex: '#7F7679' },
  { code: 'RAL 7037', name: 'Dusty Grey', hex: '#7D7F7D' },
  { code: 'RAL 7038', name: 'Agate Grey', hex: '#B5B8B1' },
  { code: 'RAL 7039', name: 'Quartz Grey', hex: '#6C6960' },
  { code: 'RAL 7040', name: 'Window Grey', hex: '#9DA1AA' },
  { code: 'RAL 7042', name: 'Traffic Grey A', hex: '#8D948D' },
  { code: 'RAL 7043', name: 'Traffic Grey B', hex: '#4E5452' },
  { code: 'RAL 7044', name: 'Silk Grey', hex: '#CAC4B0' },
  { code: 'RAL 7045', name: 'Telegrey 1', hex: '#909090' },
  { code: 'RAL 7046', name: 'Telegrey 2', hex: '#82898F' },
  { code: 'RAL 7047', name: 'Telegrey 4', hex: '#D0D0D0' },
  { code: 'RAL 7048', name: 'Pearl Mouse Grey', hex: '#898176' },

  // RAL 8xxx - Brown
  { code: 'RAL 8000', name: 'Green Brown', hex: '#826C34' },
  { code: 'RAL 8001', name: 'Ochre Brown', hex: '#955F20' },
  { code: 'RAL 8002', name: 'Signal Brown', hex: '#6C3B2A' },
  { code: 'RAL 8003', name: 'Clay Brown', hex: '#734222' },
  { code: 'RAL 8004', name: 'Copper Brown', hex: '#8E402A' },
  { code: 'RAL 8007', name: 'Fawn Brown', hex: '#59351F' },
  { code: 'RAL 8008', name: 'Olive Brown', hex: '#6F4F28' },
  { code: 'RAL 8011', name: 'Nut Brown', hex: '#5B3A29' },
  { code: 'RAL 8012', name: 'Red Brown', hex: '#592321' },
  { code: 'RAL 8014', name: 'Sepia Brown', hex: '#382C1E' },
  { code: 'RAL 8015', name: 'Chestnut Brown', hex: '#633A34' },
  { code: 'RAL 8016', name: 'Mahogany Brown', hex: '#4C2F27' },
  { code: 'RAL 8017', name: 'Chocolate Brown', hex: '#45322E' },
  { code: 'RAL 8019', name: 'Grey Brown', hex: '#403A3A' },
  { code: 'RAL 8022', name: 'Black Brown', hex: '#212121' },
  { code: 'RAL 8023', name: 'Orange Brown', hex: '#A65E2E' },
  { code: 'RAL 8024', name: 'Beige Brown', hex: '#79553D' },
  { code: 'RAL 8025', name: 'Pale Brown', hex: '#755C48' },
  { code: 'RAL 8028', name: 'Terra Brown', hex: '#4E3B31' },
  { code: 'RAL 8029', name: 'Pearl Copper', hex: '#763C28' },

  // RAL 9xxx - White/Black
  { code: 'RAL 9001', name: 'Cream', hex: '#FDF4E3' },
  { code: 'RAL 9002', name: 'Grey White', hex: '#E7EBDA' },
  { code: 'RAL 9003', name: 'Signal White', hex: '#F4F4F4' },
  { code: 'RAL 9004', name: 'Signal Black', hex: '#282828' },
  { code: 'RAL 9005', name: 'Jet Black', hex: '#0A0A0A' },
  { code: 'RAL 9006', name: 'White Aluminium', hex: '#A5A5A5' },
  { code: 'RAL 9007', name: 'Grey Aluminium', hex: '#8F8F8F' },
  { code: 'RAL 9010', name: 'Pure White', hex: '#FFFFFF' },
  { code: 'RAL 9011', name: 'Graphite Black', hex: '#1C1C1C' },
  { code: 'RAL 9016', name: 'Traffic White', hex: '#F6F6F6' },
  { code: 'RAL 9017', name: 'Traffic Black', hex: '#1E1E1E' },
  { code: 'RAL 9018', name: 'Papyrus White', hex: '#D7D7D7' },
  { code: 'RAL 9022', name: 'Pearl Light Grey', hex: '#9C9C9C' },
  { code: 'RAL 9023', name: 'Pearl Dark Grey', hex: '#828282' }
];

// BACKGROUND SCENES - Adaptive backgrounds based on frame size
// Auto-switches between close/medium/wide views as frame dimensions change
// Using ultrawide (21:9) images for WhiteWall-style full-screen experience
const BACKGROUND_OPTIONS = {
  warm: [
    {
      id: 1,
      name: 'Warm Close',
      tone: 'warm',
      distance: 'close',
      url: '/backgrounds/warm-close-ultrawide.png',
      sizeRange: [50, 70], // Optimal for 50-70cm frames
      framePosition: 'center',
      scaleFactor: 1.38,
      positionOffset: { top: -70, left: -120 }
    },
    {
      id: 2,
      name: 'Warm Medium',
      tone: 'warm',
      distance: 'medium',
      url: '/backgrounds/warm-medium-ultrawide.png',
      sizeRange: [70, 100], // Optimal for 70-100cm frames
      framePosition: 'center',
      scaleFactor: 2.06,
      positionOffset: { top: -145, left: -17 }
    },
    {
      id: 3,
      name: 'Warm Wide',
      tone: 'warm',
      distance: 'wide',
      url: '/backgrounds/warm-ultrawide.png',
      sizeRange: [100, 130], // Optimal for 100-130cm frames
      framePosition: 'center',
      scaleFactor: 1.78,
      positionOffset: { top: -70, left: -12 }
    },
    {
      id: 4,
      name: 'Warm Extra Wide',
      tone: 'warm',
      distance: 'extra-wide',
      url: '/backgrounds/warm-extra-wide.png',
      sizeRange: [130, 400], // Optimal for 130-400cm frames
      framePosition: 'center',
      scaleFactor: 3.59,
      positionOffset: { top: -50, left: -65 }
    }
  ],
  cool: [
    {
      id: 5,
      name: 'Cool Close',
      tone: 'cool',
      distance: 'close',
      url: '/backgrounds/cool-close-ultrawide.png',
      sizeRange: [50, 70], // Optimal for 50-70cm frames
      framePosition: 'center',
      scaleFactor: 1.38,
      positionOffset: { top: -85, left: -120 }
    },
    {
      id: 6,
      name: 'Cool Medium',
      tone: 'cool',
      distance: 'medium',
      url: '/backgrounds/cool-medium-ultrawide.png',
      sizeRange: [70, 100], // Optimal for 70-100cm frames
      framePosition: 'center',
      scaleFactor: 2.06,
      positionOffset: { top: -135, left: 0 }
    },
    {
      id: 7,
      name: 'Cool Wide',
      tone: 'cool',
      distance: 'wide',
      url: '/backgrounds/cool-ultrawide.png',
      sizeRange: [100, 130], // Optimal for 100-130cm frames
      framePosition: 'center',
      scaleFactor: 1.78,
      positionOffset: { top: -80, left: 0 }
    },
    {
      id: 8,
      name: 'Cool Extra Wide',
      tone: 'cool',
      distance: 'extra-wide',
      url: '/backgrounds/cool-extra-wide.png',
      sizeRange: [130, 400], // Optimal for 130-400cm frames
      framePosition: 'center',
      scaleFactor: 4.12,
      positionOffset: { top: -40, left: -65 }
    }
  ],
  none: {
    id: 0,
    name: 'No Background',
    url: null,
    framePosition: 'center',
    scaleFactor: 1.8,
    backgroundColor: '#FAFAFA',
    positionOffset: { top: -20, left: 0 }
  }
};

// Frame overlap on artwork (15mm each side)
const FRAME_OVERLAP_CM = 1.5;

// Find nearest RAL color by HEX
const findNearestRAL = (hexColor) => {
  // Convert HEX to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Calculate color distance using Euclidean distance in RGB space
  const colorDistance = (rgb1, rgb2) => {
    return Math.sqrt(
      Math.pow(rgb1.r - rgb2.r, 2) +
      Math.pow(rgb1.g - rgb2.g, 2) +
      Math.pow(rgb1.b - rgb2.b, 2)
    );
  };

  const targetRgb = hexToRgb(hexColor);
  if (!targetRgb) return null;

  let nearestRAL = RAL_COLORS[0];
  let minDistance = Infinity;

  RAL_COLORS.forEach(ralColor => {
    const ralRgb = hexToRgb(ralColor.hex);
    if (ralRgb) {
      const distance = colorDistance(targetRgb, ralRgb);
      if (distance < minDistance) {
        minDistance = distance;
        nearestRAL = ralColor;
      }
    }
  });

  return nearestRAL;
};

// Collapsible Section Component (extracted to prevent re-mounting on state changes)
const CollapsibleSection = ({ title, isExpanded, onToggle, children, badge, rightText }) => (
  <div style={{
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    transition: 'box-shadow 0.3s ease',
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: isExpanded ? 10 : 1,
    boxShadow: isExpanded ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
    marginBottom: '10px',
    flexShrink: 0
  }}>
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%',
        minHeight: '48px',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: isExpanded ? '#f9fafb' : '#fff',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.25s ease',
        fontWeight: '600',
        fontSize: '14px',
        color: '#1f2937'
      }}
      onMouseEnter={(e) => {
        if (!isExpanded) e.currentTarget.style.backgroundColor = '#f9fafb';
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) e.currentTarget.style.backgroundColor = '#fff';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{title}</span>
        {badge && (
          <span style={{
            fontSize: '10px',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: '#D4AF37',
            color: '#fff',
            fontWeight: '600'
          }}>
            {badge}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {rightText && (
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            {rightText}
          </span>
        )}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </button>
    <div style={{
      maxHeight: isExpanded ? '1000px' : '0',
      opacity: isExpanded ? 1 : 0,
      overflow: 'hidden',
      transition: 'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease',
      borderTop: isExpanded ? '1px solid #e5e7eb' : '1px solid transparent',
      backgroundColor: '#fff',
      borderRadius: '0 0 8px 8px'
    }}>
      {children}
    </div>
  </div>
);

export default function FrameCalculator() {
  const { t } = useTranslation('calculator');

  const [artworkWidth, setArtworkWidth] = useState('');
  const [artworkHeight, setArtworkHeight] = useState('');
  const [finish, setFinish] = useState('matte');
  const [thickness, setThickness] = useState(1.0); // cm, step 0.1, range 0.1..2.5
  const [whatToFrame, setWhatToFrame] = useState('');
  const [country, setCountry] = useState('France');
  const [userSelectedCountry, setUserSelectedCountry] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [shippingMethod, setShippingMethod] = useState('colissimo'); // 'colissimo' | 'dhl' | 'schenker'
  const [uploadedImage, setUploadedImage] = useState(null);

  // Color selection states
  const [selectedColor, setSelectedColor] = useState(POPULAR_COLORS[0]); // Default: Classic Black
  const [colorMode, setColorMode] = useState('popular'); // 'popular', 'ral', or 'custom'
  const [selectedRAL, setSelectedRAL] = useState('');
  const [customColor, setCustomColor] = useState('#1A1A1A'); // Default custom color

  // RAL search autocomplete states
  const [ralSearchQuery, setRalSearchQuery] = useState('');
  const [showRalSuggestions, setShowRalSuggestions] = useState(false);
  const [filteredRalColors, setFilteredRalColors] = useState([]);
  const ralAutocompleteRef = useRef(null);
  const whatToFrameRef = useRef(null);

  // Background tone selection state ('warm', 'cool', or 'none')
  const [backgroundTone, setBackgroundTone] = useState('warm');

  // Auto-selected background based on frame size and tone
  const [selectedBackground, setSelectedBackground] = useState(BACKGROUND_OPTIONS.warm[0]);

  // Collapsible sections state (for compact layout)
  const [dimensionsExpanded, setDimensionsExpanded] = useState(false);
  const [deliveryExpanded, setDeliveryExpanded] = useState(false);
  const [pricingExpanded, setPricingExpanded] = useState(false);

  // Toggle section with accordion behavior (close others when opening one)
  const toggleSection = (section) => {
    if (section === 'dimensions') {
      const newExpanded = !dimensionsExpanded;
      setDimensionsExpanded(newExpanded);
      analytics.trackSectionToggle('Frame Dimensions', newExpanded);
      if (newExpanded) {
        setDeliveryExpanded(false);
        setPricingExpanded(false);
      }
    } else if (section === 'delivery') {
      const newExpanded = !deliveryExpanded;
      setDeliveryExpanded(newExpanded);
      analytics.trackSectionToggle('Delivery Options', newExpanded);
      if (newExpanded) {
        setDimensionsExpanded(false);
        setPricingExpanded(false);
        // User opened delivery section - confirm country selection
        setUserSelectedCountry(true);
      }
    } else if (section === 'pricing') {
      const newExpanded = !pricingExpanded;
      setPricingExpanded(newExpanded);
      analytics.trackSectionToggle('Pricing Breakdown', newExpanded);
      if (newExpanded) {
        setDimensionsExpanded(false);
        setDeliveryExpanded(false);
      }
    }
  };

  // Validation states
  const [widthError, setWidthError] = useState('');
  const [heightError, setHeightError] = useState('');

  // Checkout states
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Customer and shipping info
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [shippingAddress1, setShippingAddress1] = useState('');
  const [shippingAddress2, setShippingAddress2] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingZip, setShippingZip] = useState('');

  // Auto-select appropriate background based on frame dimensions and selected tone
  useEffect(() => {
    if (backgroundTone === 'none') {
      setSelectedBackground(BACKGROUND_OPTIONS.none);
      return;
    }

    const width = parseFloat(artworkWidth) || 0;
    const height = parseFloat(artworkHeight) || 0;

    if (width === 0 || height === 0) return;

    // Use the larger dimension to determine which background to use
    const largerDimension = Math.max(width, height);

    const backgrounds = BACKGROUND_OPTIONS[backgroundTone];

    // Find the appropriate background based on size range
    let appropriateBackground = backgrounds[0]; // Default to close-up

    for (const bg of backgrounds) {
      const [minSize, maxSize] = bg.sizeRange;
      if (largerDimension >= minSize && largerDimension <= maxSize) {
        appropriateBackground = bg;
        break;
      }
      // If larger than all ranges, use the widest view
      if (largerDimension > maxSize) {
        appropriateBackground = bg;
      }
    }

    setSelectedBackground(appropriateBackground);
  }, [artworkWidth, artworkHeight, backgroundTone]);

  // Auto-switch shipping method when current one is unavailable for selected country
  // Note: The actual carrier calc objects (colissimoCalc, dhlCalc, schenkerCalc) are computed
  // later in the render, so we use the support check functions here instead.
  // The shippingCalc fallback logic below handles cases where dimension/weight limits apply.
  useEffect(() => {
    if (country) {
      const colissimoSupported = isColissimoSupported(country);
      const dhlSupported = isDHLSupported(country);
      const schenkerSupported = isSchenkerSupported(country, 30); // Assume max weight for check

      const currentSupported =
        (shippingMethod === 'colissimo' && colissimoSupported) ||
        (shippingMethod === 'dhl' && dhlSupported) ||
        (shippingMethod === 'schenker' && schenkerSupported);

      if (!currentSupported) {
        if (colissimoSupported) {
          setShippingMethod('colissimo');
        } else if (dhlSupported) {
          setShippingMethod('dhl');
        } else if (schenkerSupported) {
          setShippingMethod('schenker');
        }
      }
    }
  }, [country, shippingMethod]);

  // Close RAL autocomplete dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ralAutocompleteRef.current && !ralAutocompleteRef.current.contains(event.target)) {
        setShowRalSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Validate inputs
  const validateWidth = (value) => {
    if (value < 50) {
      setWidthError(t('validation.minWidth'));
    } else if (value > 300) {
      setWidthError(t('validation.maxWidth'));
    } else {
      setWidthError('');
    }
  };

  const validateHeight = (value) => {
    if (value < 50) {
      setHeightError(t('validation.minHeight'));
    } else if (value > 200) {
      setHeightError(t('validation.maxHeight'));
    } else {
      setHeightError('');
    }
  };

  const handleWidthChange = (e) => {
    const inputValue = e.target.value;
    setArtworkWidth(inputValue);
    const value = parseFloat(inputValue);
    if (!isNaN(value) && value > 0) {
      validateWidth(value);
    } else {
      setWidthError('');
    }
  };

  const handleHeightChange = (e) => {
    const inputValue = e.target.value;
    setArtworkHeight(inputValue);
    const value = parseFloat(inputValue);
    if (!isNaN(value) && value > 0) {
      validateHeight(value);
    } else {
      setHeightError('');
    }
  };

  // Track dimension changes with debounce
  const trackDimensionsDebounced = useCallback(() => {
    const width = parseFloat(artworkWidth);
    const height = parseFloat(artworkHeight);
    if (width > 0 && height > 0) {
      const isValid = !widthError && !heightError;
      analytics.trackDimensionChange(width, height, isValid);
    }
  }, [artworkWidth, artworkHeight, widthError, heightError]);

  // Debounce dimension tracking
  useEffect(() => {
    const timer = setTimeout(() => {
      trackDimensionsDebounced();
    }, 1000);
    return () => clearTimeout(timer);
  }, [trackDimensionsDebounced]);

  const handleInputFocus = (e) => {
    e.target.select();
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      analytics.trackImageUpload('started', file.size, file.type);
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        analytics.trackImageUpload('completed', file.size, file.type);
      };
      reader.onerror = () => {
        analytics.trackImageUpload('failed', file.size, file.type);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileUpload = () => {
    document.getElementById('fileInput').click();
  };

  // Handle color selection
  const handlePopularColorSelect = (color) => {
    setSelectedColor(color);
    setColorMode('popular');
    analytics.trackColorSelected('popular', color.name, color.hex);
  };

  // Handle RAL search input
  const handleRalSearchChange = (e) => {
    const query = e.target.value;
    setRalSearchQuery(query);

    if (query.trim() === '') {
      setFilteredRalColors([]);
      setShowRalSuggestions(false);
      return;
    }

    // Filter RAL colors by code or name
    const filtered = RAL_COLORS.filter(ral => {
      const searchLower = query.toLowerCase();
      const codeMatch = ral.code.toLowerCase().includes(searchLower);
      const nameMatch = ral.name.toLowerCase().includes(searchLower);
      // Also match just the number part (e.g., "9005" matches "RAL 9005")
      const numberMatch = ral.code.replace('RAL ', '').includes(query);
      return codeMatch || nameMatch || numberMatch;
    }).slice(0, 10); // Limit to 10 suggestions

    setFilteredRalColors(filtered);
    setShowRalSuggestions(filtered.length > 0);
  };

  // Handle RAL color selection from suggestions
  const handleRalColorSelect = (ralColor) => {
    setSelectedRAL(ralColor.code);
    setRalSearchQuery(`${ralColor.code} - ${ralColor.name}`);
    setSelectedColor({ name: ralColor.name, hex: ralColor.hex, ral: ralColor.code });
    setColorMode('ral');
    setShowRalSuggestions(false);
    analytics.trackColorSelected('ral', ralColor.name, ralColor.code);
  };

  // Calculate all values using formulas
  const widthValue = parseFloat(artworkWidth);
  const heightValue = parseFloat(artworkHeight);
  const hasValidDimensions = !isNaN(widthValue) && !isNaN(heightValue) && widthValue >= 50 && heightValue >= 50 && widthValue <= 300 && heightValue <= 200;


  const calculations = hasValidDimensions ? calculateFrame(widthValue, heightValue) : {
    frameWidth: 0,
    frameOuterWidth: 0,
    frameOuterHeight: 0,
    frameWeight: 0,
    framePriceHT: 0,
    boxOuterWidth: 0,
    boxOuterHeight: 0,
    boxThickness: 0,
    boxWeight: 0,
    totalShippingWeight: 0
  };
  const roundedFramePriceHT = roundToNearestTen(calculations.framePriceHT);

  // Calculate shipping cost for both carriers
  const boxDimensions = {
    width: calculations.boxOuterWidth,
    height: calculations.boxOuterHeight,
    thickness: calculations.boxThickness
  };

  const colissimoCalc = country ? calculateShippingCost(
    calculations.totalShippingWeight,
    country,
    boxDimensions
  ) : null;

  const dhlCalc = country ? calculateDHLShippingCost(
    calculations.totalShippingWeight,
    country,
    boxDimensions
  ) : null;

  const schenkerCalc = country ? calculateSchenkerShippingCost(
    calculations.totalShippingWeight,
    country,
    boxDimensions,
    postalCode
  ) : null;

  // Use selected shipping method, fallback to available one
  let shippingCalc;
  if (shippingMethod === 'schenker' && schenkerCalc) {
    shippingCalc = schenkerCalc;
  } else if (shippingMethod === 'dhl' && dhlCalc) {
    shippingCalc = dhlCalc;
  } else if (shippingMethod === 'colissimo' && colissimoCalc) {
    shippingCalc = colissimoCalc;
  } else {
    // Fallback: pick first available
    shippingCalc = colissimoCalc || dhlCalc || schenkerCalc;
  }
  const roundedShippingCostHT = shippingCalc ? roundToNearestTen(shippingCalc.total) : 0;

  // Calculate VAT
  const vatCalc = country && shippingCalc ? calculateVAT(
    roundedFramePriceHT,
    roundedShippingCostHT,
    country
  ) : null;

  // Handle Order Frame button click - shows modal first
  const handleOrderFrame = () => {
    // Reset error state
    setCheckoutError(null);

    // Track order button click
    analytics.trackOrderEvent('button_clicked', {
      width: widthValue,
      height: heightValue,
      color: selectedColor?.name,
      country,
      carrier: shippingMethod,
      frame_price: roundedFramePriceHT,
      shipping_cost: roundedShippingCostHT,
      total: vatCalc?.totalTTC || (roundedFramePriceHT + roundedShippingCostHT)
    });

    // Validate that all required fields are filled
    if (!widthValue || !heightValue || widthError || heightError) {
      setCheckoutError(t('validation.invalidDimensions'));
      analytics.trackError('validation', 'Invalid dimensions');
      return;
    }

    if (!selectedColor) {
      setCheckoutError(t('validation.selectColor'));
      analytics.trackError('validation', 'No color selected');
      return;
    }

    if (!country) {
      setCheckoutError(t('validation.selectCountry'));
      analytics.trackError('validation', 'No country selected');
      return;
    }

    if (!whatToFrame.trim()) {
      setCheckoutError(t('validation.whatToFrameRequired'));
      window.setTimeout(() => {
        whatToFrameRef.current?.focus();
      }, 0);
      analytics.trackError('validation', 'Missing framing intent');
      return;
    }

    // Show confirmation modal
    analytics.trackOrderEvent('modal_opened');
    setShowOrderModal(true);
  };

  // Process checkout after modal confirmation
  const proceedToCheckout = async () => {
    // Validate customer info
    if (!customerEmail || !customerEmail.includes('@')) {
      setCheckoutError('Please enter a valid email address.');
      analytics.trackError('validation', 'Invalid email');
      return;
    }

    if (!customerName || customerName.trim().length < 2) {
      setCheckoutError('Please enter your full name.');
      analytics.trackError('validation', 'Invalid name');
      return;
    }

    if (!shippingAddress1 || !shippingCity || !shippingZip) {
      setCheckoutError('Please fill in all required shipping address fields.');
      analytics.trackError('validation', 'Incomplete address');
      return;
    }

    // Track form submission
    analytics.trackOrderEvent('form_submitted', {
      country,
      carrier: shippingMethod
    });

    // Set loading state
    setIsCheckoutLoading(true);

    // Open a new window immediately (before async call) to avoid popup blocking
    const checkoutWindow = window.open('about:blank', '_blank');

    try {
      // Prepare data for serverless function
      const orderData = {
        artworkWidth: widthValue,
        artworkHeight: heightValue,
        whatToFrame: whatToFrame.trim(),
        finish: finish,
        thickness: thickness,
        color: {
          name: selectedColor.name,
          hex: selectedColor.hex,
          ral: selectedColor.ral
        },
        country: country,
        postalCode: postalCode || shippingZip,
        calculations: {
          frameOuterWidth: calculations.frameOuterWidth,
          frameOuterHeight: calculations.frameOuterHeight,
          frameSurfaceArea: calculations.frameSurfaceArea,
          frameWeight: calculations.frameWeight,
          rawFramePriceHT: calculations.framePriceHT,
          framePriceHT: roundedFramePriceHT,
          boxOuterWidth: calculations.boxOuterWidth,
          boxOuterHeight: calculations.boxOuterHeight,
          boxThickness: calculations.boxThickness,
          totalShippingWeight: calculations.totalShippingWeight,
          rawShippingCost: shippingCalc ? shippingCalc.total : 0,
          shippingCost: roundedShippingCostHT,
          subtotalHT: vatCalc ? vatCalc.subtotalHT : roundedFramePriceHT + roundedShippingCostHT,
          frameVatRate: vatCalc ? vatCalc.frameVatRate : 0,
          shippingVatRate: vatCalc ? vatCalc.shippingVatRate : 0,
          frameVatAmount: vatCalc ? vatCalc.frameVatAmount : 0,
          shippingVatAmount: vatCalc ? vatCalc.shippingVatAmount : 0,
          vatAmount: vatCalc ? vatCalc.vatAmount : 0,
          rawTotalTTC: vatCalc ? vatCalc.rawTotalTTC : roundedFramePriceHT + roundedShippingCostHT,
          totalTTC: vatCalc ? vatCalc.totalTTC : roundedFramePriceHT + roundedShippingCostHT
        },
        // Customer information
        customerEmail: customerEmail,
        customerName: customerName,
        shippingAddress: {
          name: customerName,
          address1: shippingAddress1,
          address2: shippingAddress2 || undefined,
          city: shippingCity,
          zip: shippingZip,
          country: country
        }
      };

      // Call Stripe checkout serverless function
      const response = await axios.post('/api/create-stripe-checkout', orderData);

      console.log('Stripe checkout response:', response.data);

      if (response.data.success && response.data.checkoutUrl) {
        // Track payment started
        analytics.trackOrderEvent('payment_started', {
          order_total: vatCalc ? vatCalc.totalTTC : roundedFramePriceHT + roundedShippingCostHT,
          currency: 'EUR',
          carrier: shippingMethod,
          country
        });

        // Navigate the pre-opened window to Stripe checkout
        if (checkoutWindow) {
          checkoutWindow.location.href = response.data.checkoutUrl;
        } else {
          // Fallback if popup was blocked
          window.open(response.data.checkoutUrl, '_blank');
        }
        // Reset loading state after opening checkout
        setIsCheckoutLoading(false);
      } else {
        // Close the blank window if checkout creation failed
        if (checkoutWindow) {
          checkoutWindow.close();
        }
        throw new Error('Failed to create checkout');
      }

    } catch (error) {
      // Close the blank window if error occurred
      if (checkoutWindow) {
        checkoutWindow.close();
      }
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);

      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create checkout. Please try again.';

      // Track order failed
      analytics.trackOrderEvent('failed', {
        error_message: errorMessage,
        error_code: error.response?.status
      });

      setCheckoutError(errorMessage);
      setIsCheckoutLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      minHeight: '85vh',
      maxHeight: '900px',
      padding: '20px'
    }}>
      {/* Centered container: Photo + Panel */}
      <div style={{
        display: 'flex',
        maxWidth: '100%',
        maxHeight: '100%',
        minWidth: 'auto',
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.15)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        {/* LEFT PART: Photo with Frame overlay */}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '1 1 auto',
          minWidth: '400px',
          minHeight: 'auto',
          width: 'auto',
          backgroundColor: selectedBackground.backgroundColor || 'transparent'
        }}>
          {/* Background Image - using <img> for proper sizing */}
          {selectedBackground.url ? (
            <img
              src={selectedBackground.url}
              alt="Interior background"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          ) : (
            /* Invisible placeholder to match background image dimensions (1536x1024 = 1.5:1 ratio) */
            <div style={{
              width: 'calc(80vh * 1.5)',
              height: '80vh',
              maxWidth: '100%'
            }} />
          )}

          {/* Studio floor line for "none" background - subtle spatial reference */}
          {!selectedBackground.url && (
            <div style={{
              position: 'absolute',
              bottom: '25%',
              left: '10%',
              right: '10%',
              height: '1px',
              background: 'linear-gradient(to right, transparent, rgba(0, 0, 0, 0.06), transparent)',
              pointerEvents: 'none'
            }} />
          )}

          {/* Frame overlay - positioned over the image */}
          <div className="preview-frame" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none'
          }}>

            {/* Frame Preview - Dynamic size with proportional frame */}
            {(() => {
              // Calculate preview dimensions with proper scaling
              const maxPreviewSize = 450; // Increased for larger frame preview
              const displayScaleFactor = 1.2; // Scale down frames to fit better in view

              const frameW = hasValidDimensions ? calculations.frameOuterWidth : 64; // 50 + 7cm frame each side
              const frameH = hasValidDimensions ? calculations.frameOuterHeight : 64; // 50 + 7cm frame each side
              const artW = hasValidDimensions ? widthValue : 50;
              const artH = hasValidDimensions ? heightValue : 50;

              // Visible artwork size (after 15mm overlap on each side)
              const visibleArtW = artW - (FRAME_OVERLAP_CM * 2);
              const visibleArtH = artH - (FRAME_OVERLAP_CM * 2);

              // Scale factor to fit in preview area (apply displayScaleFactor to make frames smaller)
              const scale = (maxPreviewSize / Math.max(frameW, frameH)) / displayScaleFactor;

              // Preview dimensions in pixels
              const previewFrameWidth = frameW * scale;
              const previewFrameHeight = frameH * scale;

              const innerWidth = visibleArtW * scale;
              const innerHeight = visibleArtH * scale;

              // Apply scale factor if background has one
              const scaleFactor = selectedBackground.scaleFactor || 1;

              let scaledFrameWidth = previewFrameWidth;
              let scaledFrameHeight = previewFrameHeight;
              let scaledInnerWidth = innerWidth;
              let scaledInnerHeight = innerHeight;

              if (scaleFactor !== 1) {
                // Baseline: 50x50cm artwork = 56x56cm frame
                const baselineFrameSize = 56;

                // Calculate individual scales for width and height independently
                const widthScale = (maxPreviewSize / baselineFrameSize) / (scaleFactor * displayScaleFactor);
                const heightScale = (maxPreviewSize / baselineFrameSize) / (scaleFactor * displayScaleFactor);

                // Apply width scale to width dimensions, height scale to height dimensions
                scaledFrameWidth = frameW * widthScale;
                scaledInnerWidth = visibleArtW * widthScale;
                scaledFrameHeight = frameH * heightScale;
                scaledInnerHeight = visibleArtH * heightScale;
              }

              // Apply position offset if background has one
              const positionOffset = selectedBackground.positionOffset || { top: 0, left: 0 };
              const topPosition = `calc(50% + ${positionOffset.top}px)`;
              const leftPosition = `calc(50% + ${positionOffset.left}px)`;

              // Enhanced shadow for "none" background (studio photography effect)
              const isNoneBackground = !selectedBackground.url;
              const frameShadow = isNoneBackground
                ? '0 25px 80px rgba(0, 0, 0, 0.18), 0 10px 30px rgba(0, 0, 0, 0.12)'
                : '0 4px 12px rgba(0,0,0,0.15)';

              const frameStyle = {
                position: 'absolute',
                top: topPosition,
                left: leftPosition,
                transform: 'translate(-50%, -50%)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: selectedColor.hex,
                borderRadius: '4px',
                width: `${scaledFrameWidth}px`,
                height: `${scaledFrameHeight}px`,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: frameShadow,
                pointerEvents: 'all'
              };

              return (
                <>
                  <div
                    className="frame-preview-outer"
                    style={frameStyle}
                    onClick={triggerFileUpload}
                  >
                    {/* Hidden file input */}
                    <input
                      id="fileInput"
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />

                    {/* Inner artwork area (visible part after frame overlap) */}
                    <div
                      className="frame-preview-inner"
                      style={{
                        width: `${scaledInnerWidth}px`,
                        height: `${scaledInnerHeight}px`,
                        backgroundColor: '#f5f5f5',
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Uploaded image or upload icon */}
                      {uploadedImage ? (
                        <img
                          src={uploadedImage}
                          alt="Uploaded artwork"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.02)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: '#9ca3af',
                            fontSize: '10px',
                            fontWeight: '500',
                            opacity: 0.7,
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                          }}
                        >
                          {t('buttons.uploadImage')}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
            {/* Size indicators - Overlay at bottom of preview */}
            {hasValidDimensions && (
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
                fontSize: '14px',
                color: '#ffffff',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                padding: '12px 24px',
                borderRadius: '8px',
                zIndex: 10,
                backdropFilter: 'blur(4px)'
              }}>
                <div style={{ fontWeight: '500' }}>Artwork: {artworkWidth} × {artworkHeight} cm</div>
                <div style={{ color: '#c9a962', fontSize: '13px', marginTop: '4px', fontWeight: '500' }}>
                  Frame: {calculations.frameOuterWidth} × {calculations.frameOuterHeight} cm
                </div>
                {/* Dynamic hint for optimal image upload */}
                <div style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  textAlign: 'center'
                }}>
                  {(() => {
                    // Calculate aspect ratio
                    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
                    const divisor = gcd(Math.round(widthValue * 10), Math.round(heightValue * 10));
                    const ratioW = Math.round(widthValue * 10) / divisor;
                    const ratioH = Math.round(heightValue * 10) / divisor;

                    // Suggest pixel dimensions
                    let suggestedWidth = 1200;
                    if (widthValue > heightValue) {
                      suggestedWidth = 1600;
                    } else if (widthValue < heightValue) {
                      suggestedWidth = 1200;
                    } else {
                      suggestedWidth = 1200;
                    }

                    const suggestedHeight = Math.round(suggestedWidth * (heightValue / widthValue));

                    return (
                      <span><strong>{t('help.uploadImageHint')}</strong> {ratioW}:{ratioH} ratio (e.g., {suggestedWidth} × {suggestedHeight} px)</span>
                    );
                  })()}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* RIGHT PART: Controls Panel - attached to photo */}
        <div className="calculator-panel" style={{
          width: '340px',
          flexShrink: 0,
          maxHeight: '80vh',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Frame Color Selection */}
          <div style={{ marginBottom: '12px', flexShrink: 0 }}>
            <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>{t('sections.frameColor')}</label>

            {/* Color Mode Tabs */}
            <div style={{
              display: 'flex',
              gap: '6px',
              marginBottom: '12px',
              borderBottom: '2px solid #e5e7eb',
              paddingBottom: '0'
            }}>
              <button
                type="button"
                onClick={() => setColorMode('popular')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: colorMode === 'popular' ? '3px solid #D4AF37' : '3px solid transparent',
                  background: colorMode === 'popular' ? '#f9fafb' : 'transparent',
                  color: colorMode === 'popular' ? '#1f2937' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: colorMode === 'popular' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
              >
                {t('tabs.popular')}
              </button>
              <button
                type="button"
                onClick={() => setColorMode('ral')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: colorMode === 'ral' ? '3px solid #D4AF37' : '3px solid transparent',
                  background: colorMode === 'ral' ? '#f9fafb' : 'transparent',
                  color: colorMode === 'ral' ? '#1f2937' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: colorMode === 'ral' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
              >
                {t('tabs.ralCode')}
              </button>
              <button
                type="button"
                onClick={() => setColorMode('custom')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: colorMode === 'custom' ? '3px solid #D4AF37' : '3px solid transparent',
                  background: colorMode === 'custom' ? '#f9fafb' : 'transparent',
                  color: colorMode === 'custom' ? '#1f2937' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: colorMode === 'custom' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
              >
                {t('tabs.custom')}
              </button>
            </div>

            {/* Popular Colors */}
            {colorMode === 'popular' && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>{t('labels.popularColors')}</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '6px'
              }}>
                {POPULAR_COLORS.map((color) => (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => handlePopularColorSelect(color)}
                    style={{
                      width: '100%',
                      height: '36px',
                      backgroundColor: color.hex,
                      border: selectedColor.hex === color.hex && colorMode === 'popular'
                        ? '3px solid #D4AF37'
                        : '2px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: selectedColor.hex === color.hex && colorMode === 'popular'
                        ? '0 0 0 3px rgba(212, 175, 55, 0.2)'
                        : 'none',
                      position: 'relative'
                    }}
                    title={color.name}
                    onMouseEnter={(e) => {
                      if (selectedColor.hex !== color.hex || colorMode !== 'popular') {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      if (selectedColor.hex !== color.hex || colorMode !== 'popular') {
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  />
                ))}
              </div>
              <div style={{
                fontSize: '10px',
                color: '#9ca3af',
                marginTop: '6px',
                textAlign: 'center',
                minHeight: '14px'
              }}>
                {selectedColor.name}
              </div>
            </div>
            )}

            {/* RAL Color Selector with Autocomplete */}
            {colorMode === 'ral' && (
            <div ref={ralAutocompleteRef} style={{ position: 'relative' }}>
              <label htmlFor="ralColor" style={{ fontSize: '14px', color: '#6b7280', marginBottom: '6px', display: 'block' }}>
                {t('labels.searchRalColor')}
              </label>
              <input
                type="text"
                id="ralColor"
                value={ralSearchQuery}
                onChange={handleRalSearchChange}
                onFocus={() => {
                  if (filteredRalColors.length > 0) {
                    setShowRalSuggestions(true);
                  }
                }}
                placeholder={t('placeholders.ralSearch')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  outline: 'none'
                }}
              />

              {/* Autocomplete Suggestions Dropdown */}
              {showRalSuggestions && filteredRalColors.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 1000
                }}>
                  {filteredRalColors.map((ral) => (
                    <div
                      key={ral.code}
                      onClick={() => handleRalColorSelect(ral)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: ral.hex,
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        flexShrink: 0
                      }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>
                          {ral.code}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#6b7280'
                        }}>
                          {ral.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Color Display */}
              {selectedColor && selectedRAL && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  border: '2px solid #e5e7eb',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: selectedColor.hex,
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb'
                  }}></div>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{selectedColor.ral} - {selectedColor.name}</span>
                </div>
              )}
            </div>
            )}

            {/* Custom Color Picker */}
            {colorMode === 'custom' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label htmlFor="customColor" style={{ fontSize: '14px', color: '#6b7280' }}>
                  {t('labels.chooseCustomColor')}
                </label>

                {/* Eyedropper Button - Uses native browser EyeDropper API */}
                <button
                  type="button"
                  onClick={async () => {
                    // Check if EyeDropper API is supported
                    if (!window.EyeDropper) {
                      alert('EyeDropper API is not supported in your browser. Please use Chrome, Edge, or Opera.');
                      return;
                    }

                    try {
                      const eyeDropper = new window.EyeDropper();
                      const result = await eyeDropper.open();

                      if (result && result.sRGBHex) {
                        setCustomColor(result.sRGBHex);
                        setSelectedColor({ hex: result.sRGBHex, name: 'Picked Color', ral: 'Custom' });
                      }
                    } catch (err) {
                      // User cancelled the picker or an error occurred
                      console.log('EyeDropper cancelled or error:', err);
                    }
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#6b7280',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease'
                  }}
                  title={t('help.pickColorFromScreen')}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.5 2L18 6.5L8.5 16H4.5V12L13.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.5 4L16 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 20H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {t('buttons.pickColor')}
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                {/* Color Picker Input */}
                <input
                  type="color"
                  id="customColor"
                  value={customColor}
                  onChange={(e) => {
                    setCustomColor(e.target.value);
                    setSelectedColor({ hex: e.target.value, name: 'Custom Color', ral: 'Custom' });
                  }}
                  style={{
                    width: '60px',
                    height: '60px',
                    border: '3px solid #D4AF37',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: customColor
                  }}
                />

                {/* Color Info Display */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    padding: '8px 12px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    border: '2px solid #e5e7eb',
                    display: 'flex',
                    gap: '12px'
                  }}>
                    {/* Selected Color - Left Side */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        {t('labels.selectedColor')}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: customColor,
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb'
                        }}></div>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#1f2937',
                          fontFamily: 'monospace'
                        }}>
                          {customColor.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {/* Nearest RAL Color - Right Side (Clickable) */}
                    {(() => {
                      const nearestRAL = findNearestRAL(customColor);
                      return nearestRAL ? (
                        <div style={{
                          flex: 1,
                          paddingLeft: '12px',
                          borderLeft: '1px solid #e5e7eb'
                        }}>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            {t('labels.nearestRal')}
                          </div>
                          <div
                            onClick={() => {
                              setSelectedColor({
                                name: nearestRAL.name,
                                hex: nearestRAL.hex,
                                ral: nearestRAL.code
                              });
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              border: '1px solid transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                              e.currentTarget.style.borderColor = '#D4AF37';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = 'transparent';
                            }}
                            title={t('help.clickToUseRal')}
                          >
                            <div style={{
                              width: '24px',
                              height: '24px',
                              backgroundColor: nearestRAL.hex,
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb'
                            }}></div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#1f2937'
                              }}>
                                {nearestRAL.code}
                              </div>
                              <div style={{
                                fontSize: '9px',
                                color: '#6b7280'
                              }}>
                                {nearestRAL.name}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div style={{
                padding: '10px 12px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#92400e',
                lineHeight: '1.5'
              }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '14px' }}>⚠️</span>
                  <div>
                    {t('validation.colorCalibrationWarning')}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Background Tone Selector - Tab Style */}
          <div style={{ marginBottom: '12px', flexShrink: 0 }}>
            <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
              {t('sections.backgroundTone')}
            </label>
            <div style={{
              display: 'flex',
              gap: '6px',
              marginBottom: '12px',
              borderBottom: '2px solid #e5e7eb',
              paddingBottom: '0'
            }}>
              <button
                type="button"
                onClick={() => {
                  setBackgroundTone('warm');
                  analytics.track(EVENTS.CALCULATOR_BACKGROUND_TONE_CHANGED, { tone: 'warm' });
                }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: backgroundTone === 'warm' ? '3px solid #D4AF37' : '3px solid transparent',
                  background: backgroundTone === 'warm' ? '#f9fafb' : 'transparent',
                  color: backgroundTone === 'warm' ? '#1f2937' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: backgroundTone === 'warm' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
                title={t('help.warmToneInterior')}
              >
                {t('tabs.warm')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setBackgroundTone('cool');
                  analytics.track(EVENTS.CALCULATOR_BACKGROUND_TONE_CHANGED, { tone: 'cool' });
                }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: backgroundTone === 'cool' ? '3px solid #D4AF37' : '3px solid transparent',
                  background: backgroundTone === 'cool' ? '#f9fafb' : 'transparent',
                  color: backgroundTone === 'cool' ? '#1f2937' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: backgroundTone === 'cool' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
                title={t('help.coolToneInterior')}
              >
                {t('tabs.cool')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setBackgroundTone('none');
                  analytics.track(EVENTS.CALCULATOR_BACKGROUND_TONE_CHANGED, { tone: 'none' });
                }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: backgroundTone === 'none' ? '3px solid #D4AF37' : '3px solid transparent',
                  background: backgroundTone === 'none' ? '#f9fafb' : 'transparent',
                  color: backgroundTone === 'none' ? '#1f2937' : '#6b7280',
                  fontSize: '14px',
                  fontWeight: backgroundTone === 'none' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
                title={t('help.noBackground')}
              >
                {t('tabs.none')}
              </button>
            </div>
          </div>

        {/* Scrollable area for collapsible sections */}
        <div className="scrollable-sections" style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
        {/* Frame Options - Collapsible */}
        <CollapsibleSection
          title={t('sections.frameOptions')}
          isExpanded={dimensionsExpanded}
          onToggle={() => toggleSection('dimensions')}
          badge={hasValidDimensions ? `${widthValue}×${heightValue}cm` : null}
        >
          <div style={{ padding: '16px' }}>
            {/* Width Input */}
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="artworkWidth" style={{
                display: 'block',
                fontSize: '15px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.width')}</label>
              <input
                type="number"
                id="artworkWidth"
                min="50"
                max="300"
                value={artworkWidth}
                onChange={handleWidthChange}
                onFocus={handleInputFocus}
                className={widthError ? 'error' : ''}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
              {widthError && (
                <span className="error-message">{widthError}</span>
              )}
            </div>

            {/* Height Input */}
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="artworkHeight" style={{
                display: 'block',
                fontSize: '15px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.height')}</label>
              <input
                type="number"
                id="artworkHeight"
                min="50"
                max="200"
                value={artworkHeight}
                onChange={handleHeightChange}
                onFocus={handleInputFocus}
                className={heightError ? 'error' : ''}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
              {heightError && (
                <span className="error-message">{heightError}</span>
              )}
            </div>

            {/* Thickness */}
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="frameThickness" style={{
                display: 'block',
                fontSize: '15px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.thickness')}</label>
              <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                <button
                  type="button"
                  aria-label="Decrease thickness"
                  onClick={() => setThickness(prev => Math.max(0.1, Math.round((prev - 0.1) * 10) / 10))}
                  style={{
                    padding: '0 14px',
                    fontSize: '18px',
                    fontWeight: '600',
                    backgroundColor: '#f9fafb',
                    border: 'none',
                    borderRight: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >−</button>
                <input
                  type="number"
                  id="frameThickness"
                  min="0.1"
                  max="2.5"
                  step="0.1"
                  value={thickness.toFixed(1)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val)) {
                      const clamped = Math.min(2.5, Math.max(0.1, val));
                      setThickness(Math.round(clamped * 10) / 10);
                    }
                  }}
                  onFocus={handleInputFocus}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: 'none',
                    outline: 'none',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                />
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 10px',
                  fontSize: '13px',
                  color: '#6b7280',
                  borderLeft: '1px solid #e5e7eb',
                  borderRight: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>cm</span>
                <button
                  type="button"
                  aria-label="Increase thickness"
                  onClick={() => setThickness(prev => Math.min(2.5, Math.round((prev + 0.1) * 10) / 10))}
                  style={{
                    padding: '0 14px',
                    fontSize: '18px',
                    fontWeight: '600',
                    backgroundColor: '#f9fafb',
                    border: 'none',
                    borderLeft: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >+</button>
              </div>
            </div>

            {/* Finish Type */}
            <div>
              <label htmlFor="artworkFinish" style={{
                display: 'block',
                fontSize: '15px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.finish')}</label>
              <select
                id="artworkFinish"
                value={finish}
                onChange={(e) => {
                  const newFinish = e.target.value;
                  setFinish(newFinish);
                  analytics.track(EVENTS.CALCULATOR_FINISH_CHANGED, { finish: newFinish });
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="matte">{t('finishes.matte')}</option>
                <option value="glossy">{t('finishes.glossy')}</option>
              </select>
            </div>
          </div>
        </CollapsibleSection>

        {/* Delivery Options - Collapsible */}
        <CollapsibleSection
          title={t('sections.deliveryOptions')}
          isExpanded={deliveryExpanded}
          onToggle={() => toggleSection('delivery')}
          badge={userSelectedCountry ? (shippingCalc ? `${country} · €${roundedShippingCostHT.toFixed(0)}` : country) : null}
        >
          <div style={{ padding: '16px' }}>
            {/* Country Selection */}
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="shippingCountry" style={{
                display: 'block',
                fontSize: '11px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.shippingDestination')}</label>
              <select
                id="shippingCountry"
                value={country}
                onChange={(e) => {
                  const newCountry = e.target.value;
                  analytics.track(EVENTS.DELIVERY_COUNTRY_CHANGED, {
                    country: newCountry,
                    previous_country: country
                  });
                  setCountry(newCountry);
                  setUserSelectedCountry(true);
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
            <optgroup label={t('shippingGroups.france')}>
              <option value="France">{t('countries.france')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.franceOverseas')}>
              <option value="Guadeloupe">{t('countries.guadeloupe')}</option>
              <option value="Martinique">{t('countries.martinique')}</option>
              <option value="La Réunion">{t('countries.laReunion')}</option>
              <option value="Guyane">{t('countries.guyane')}</option>
              <option value="Mayotte">{t('countries.mayotte')}</option>
              <option value="Saint-Pierre-et-Miquelon">{t('countries.saintPierreMiquelon')}</option>
              <option value="Nouvelle-Calédonie">{t('countries.nouvelleCaledonie')}</option>
              <option value="Polynésie Française">{t('countries.polynesia')}</option>
              <option value="Wallis-et-Futuna">{t('countries.wallisFutuna')}</option>
              <option value="Terres Australes et Antarctiques Françaises">{t('countries.terresAustrales')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.europeZone1')}>
              <option value="Allemagne">{t('countries.germany')}</option>
              <option value="Belgique">{t('countries.belgium')}</option>
              <option value="Luxembourg">{t('countries.luxembourg')}</option>
              <option value="Pays-Bas">{t('countries.netherlands')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.europeZone2')}>
              <option value="Autriche">{t('countries.austria')}</option>
              <option value="Espagne">{t('countries.spain')}</option>
              <option value="Guernesey">{t('countries.guernsey')}</option>
              <option value="Italie">{t('countries.italy')}</option>
              <option value="Jersey">{t('countries.jersey')}</option>
              <option value="Portugal">{t('countries.portugal')}</option>
              <option value="République d'Irlande">{t('countries.ireland')}</option>
              <option value="Royaume-Uni">{t('countries.uk')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.europeZone3')}>
              <option value="Danemark">{t('countries.denmark')}</option>
              <option value="Estonie">{t('countries.estonia')}</option>
              <option value="Groenland">{t('countries.greenland')}</option>
              <option value="Hongrie">{t('countries.hungary')}</option>
              <option value="Îles Féroé">{t('countries.faroeIslands')}</option>
              <option value="Lettonie">{t('countries.latvia')}</option>
              <option value="Liechtenstein">{t('countries.liechtenstein')}</option>
              <option value="Lituanie">{t('countries.lithuania')}</option>
              <option value="Pologne">{t('countries.poland')}</option>
              <option value="République tchèque">{t('countries.czechRepublic')}</option>
              <option value="Slovaquie">{t('countries.slovakia')}</option>
              <option value="Slovénie">{t('countries.slovenia')}</option>
              <option value="Suède">{t('countries.sweden')}</option>
              <option value="Suisse">{t('countries.switzerland')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.europeMaghrebZone4')}>
              <option value="Bulgarie">{t('countries.bulgaria')}</option>
              <option value="Chypre">{t('countries.cyprus')}</option>
              <option value="Croatie">{t('countries.croatia')}</option>
              <option value="Finlande">{t('countries.finland')}</option>
              <option value="Grèce">{t('countries.greece')}</option>
              <option value="Islande">{t('countries.iceland')}</option>
              <option value="Malte">{t('countries.malta')}</option>
              <option value="Norvège">{t('countries.norway')}</option>
              <option value="Roumanie">{t('countries.romania')}</option>
              <option value="Turquie">{t('countries.turkey')}</option>
              <option value="Maroc">{t('countries.morocco')}</option>
              <option value="Algérie">{t('countries.algeria')}</option>
              <option value="Tunisie">{t('countries.tunisia')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.internationalZone5')}>
              <option value="Australie">{t('countries.australia')}</option>
              <option value="Canada">{t('countries.canada')}</option>
              <option value="Chine">{t('countries.china')}</option>
              <option value="Corée du Sud">{t('countries.southKorea')}</option>
              <option value="États-Unis">{t('countries.usa')}</option>
              <option value="Hong Kong">{t('countries.hongKong')}</option>
              <option value="Inde">{t('countries.india')}</option>
              <option value="Israël">{t('countries.israel')}</option>
              <option value="Japon">{t('countries.japan')}</option>
              <option value="Russie">{t('countries.russia')}</option>
              <option value="Singapour">{t('countries.singapore')}</option>
              <option value="Taiwan">{t('countries.taiwan')}</option>
              <option value="Thaïlande">{t('countries.thailand')}</option>
              <option value="Vietnam">{t('countries.vietnam')}</option>
            </optgroup>
            <optgroup label={t('shippingGroups.othersZone6')}>
              <option value="Autres pays">{t('countries.otherCountries')}</option>
            </optgroup>
              </select>
            </div>

            {/* Postal Code */}
            <div>
              <label htmlFor="postalCode" style={{
                display: 'block',
                fontSize: '11px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.postalCode')}</label>
              <input
                type="text"
                id="postalCode"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder={t('placeholders.postalCode')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Shipping Method Selection */}
            <div style={{ marginTop: '16px' }}>
              <label htmlFor="shippingMethod" style={{
                display: 'block',
                fontSize: '11px',
                color: '#6b7280',
                marginBottom: '6px',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{t('labels.shippingMethod', 'Shipping Method')}</label>
              <select
                id="shippingMethod"
                value={shippingMethod}
                onChange={(e) => {
                  const newMethod = e.target.value;
                  const calc = newMethod === 'colissimo' ? colissimoCalc : newMethod === 'dhl' ? dhlCalc : schenkerCalc;
                  if (calc) {
                    analytics.trackDeliverySelection(country, newMethod, calc.total);
                    setShippingMethod(newMethod);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                {colissimoCalc && (
                  <option value="colissimo">
                    Colissimo — €{colissimoCalc.total.toFixed(2)}
                  </option>
                )}
                {dhlCalc && (
                  <option value="dhl">
                    DHL Express — €{dhlCalc.total.toFixed(2)}
                  </option>
                )}
                {schenkerCalc && (
                  <option value="schenker">
                    DB Schenker — €{schenkerCalc.total.toFixed(2)}
                  </option>
                )}
                {!colissimoCalc && !dhlCalc && !schenkerCalc && (
                  <option value="" disabled>
                    {t('shipping.noCarrierAvailable', 'No carrier available')}
                  </option>
                )}
              </select>
              {/* Carrier description */}
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                {shippingMethod === 'colissimo' && t('shipping.standardDelivery', 'Standard delivery')}
                {shippingMethod === 'dhl' && (
                  <>{t('shipping.expressDelivery', 'Express delivery')} <span style={{ color: '#f59e0b' }}>+ fuel surcharge</span></>
                )}
                {shippingMethod === 'schenker' && t('shipping.freightDelivery', 'Freight delivery')}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Details - Collapsible (price breakdown) */}
        <CollapsibleSection
          title={t('sections.details', 'Details')}
          isExpanded={pricingExpanded}
          onToggle={() => toggleSection('pricing')}
        >
          {/* Calculation Results */}
        {(!widthError && !heightError && hasValidDimensions) ? (
          <div className="price-breakdown" style={{
            padding: '12px',
            minWidth: 'unset',
            fontSize: '12px'
          }}>
            {/* Selected Frame Color */}
            <div className="price-line" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '8px',
              marginBottom: '8px',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px'
            }}>
              <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('pricing.frameColor')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '3px',
                  backgroundColor: selectedColor.hex,
                  border: '1px solid #d1d5db',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  flexShrink: 0
                }} />
                <div style={{ textAlign: 'right', lineHeight: '1.2', overflow: 'hidden' }}>
                  <div style={{ fontWeight: '500', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selectedColor.ral === 'Custom' ? 'Picked Color' : selectedColor.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selectedColor.ral === 'Custom' ? selectedColor.hex.toUpperCase() : selectedColor.ral}
                  </div>
                </div>
              </div>
            </div>

            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.frameOuter')}</span>
              <span>{calculations.frameOuterWidth} × {calculations.frameOuterHeight} cm</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.frameSurface')}</span>
              <span>{calculations.frameSurfaceArea.toLocaleString()} cm²</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.frameWeight')}</span>
              <span>{calculations.frameWeight.toFixed(2)} kg</span>
            </div>

            {/* Frame Price */}
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.framePrice')}</span>
              <span style={{ fontWeight: 'bold' }}>€{roundedFramePriceHT.toFixed(2)}</span>
            </div>

            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.boxDimensions')}</span>
              <span style={{ fontSize: '12px' }}>{calculations.boxOuterWidth} × {calculations.boxOuterHeight} × {calculations.boxThickness} cm</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.boxWeight')}</span>
              <span>{calculations.totalBoxWeight.toFixed(2)} kg</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>{t('pricing.totalShippingWeight')}</span>
              <span>{calculations.totalShippingWeight.toFixed(2)} kg</span>
            </div>

            {/* Shipping Cost Section */}
            {shippingCalc && (
              <>
                <div className="price-line" style={{ paddingTop: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
                  <span>{t('pricing.baseShipping')}</span>
                  <span>€{roundedShippingCostHT.toFixed(2)}</span>
                </div>

                {/* Fees Breakdown */}
                {shippingCalc.fees && shippingCalc.fees.decarbonation !== undefined ? (
                  // Colissimo fees structure
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', padding: '4px 0' }}>
                    <div style={{ marginBottom: '2px' }}>{t('pricing.additionalFees')}</div>
                    <div>• {t('pricing.decarbonation')} €{shippingCalc.fees.decarbonation.toFixed(2)}</div>
                    {shippingCalc.fees.nonMechanizable > 0 && (
                      <div>• {t('pricing.nonMechanizable')} €{shippingCalc.fees.nonMechanizable.toFixed(2)}</div>
                    )}
                    {shippingCalc.fees.countrySpecific > 0 && (
                      <div>• {t('pricing.country')} €{shippingCalc.fees.countrySpecific.toFixed(2)}</div>
                    )}
                  </div>
                ) : shippingCalc.fees && shippingCalc.fees.contributionSurete !== undefined ? (
                  // Schenker fees structure
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', padding: '4px 0' }}>
                    <div style={{ marginBottom: '2px' }}>{t('pricing.additionalFees')}</div>
                    <div>• {t('pricing.securityFee', 'Contribution sûreté')} €{shippingCalc.fees.contributionSurete.toFixed(2)}</div>
                    <div>• {t('pricing.energyFee', 'Contribution énergie')} €{shippingCalc.fees.contributionEnergie.toFixed(2)}</div>
                    <div>• {t('pricing.privateDelivery', 'Livraison particulier')} €{shippingCalc.fees.livraisonParticulier.toFixed(2)}</div>
                    {shippingCalc.fees.regionParisienne > 0 && (
                      <div>• {t('pricing.parisRegion', 'Région parisienne')} €{shippingCalc.fees.regionParisienne.toFixed(2)}</div>
                    )}
                    {shippingCalc.fees.corseSurcharge > 0 && (
                      <div>• {t('pricing.corsicaSurcharge', 'Majoration Corse')} €{shippingCalc.fees.corseSurcharge.toFixed(2)}</div>
                    )}
                  </div>
                ) : (
                  // DHL - no fees breakdown, just fuel surcharge note
                  <div style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '6px', padding: '4px 0' }}>
                    <div>• {t('pricing.fuelSurcharge', 'Fuel surcharge applies')}</div>
                  </div>
                )}

                {/* VAT-aware Pricing Section */}
                {vatCalc ? (
                  <>
                    {/* Subtotal (excl. VAT) */}
                    <div className="price-line" style={{ paddingTop: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
                      <span>{t('pricing.subtotalExclVat')}</span>
                      <span>€{vatCalc.subtotalHT.toFixed(2)}</span>
                    </div>

                    {/* VAT Amount (conditional) */}
                    {vatCalc.displayVAT && (
                      <>
                        {vatCalc.displayFrameVAT && (
                          <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
                              <span>{t('pricing.frameVat', 'Frame VAT')} ({formatVatRate(vatCalc.frameVatRate)}%):</span>
                              <span>€{vatCalc.frameVatAmount.toFixed(2)}</span>
                          </div>
                        )}
                        {vatCalc.displayShippingVAT && (
                          <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
                            <span>{t('pricing.shippingVat', 'Shipping VAT')} ({formatVatRate(vatCalc.shippingVatRate)}%):</span>
                            <span>€{vatCalc.shippingVatAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Total Price with TTC/HT label */}
                    <div className="price-total" style={{ borderTop: '2px solid #D4AF37', color: '#D4AF37', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', fontSize: '20px', padding: '8px 0', marginTop: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{vatCalc.displayVAT ? t('pricing.totalInclVat') : t('pricing.totalExclVat')}</span>
                      <span style={{ fontSize: '20px' }}>€{vatCalc.totalTTC.toFixed(2)}</span>
                    </div>

                    {/* Explanatory Note */}
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', lineHeight: '1.3' }}>
                      {vatCalc.note}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Fallback when country not selected */}
                    <div className="price-total" style={{ borderTop: '2px solid #D4AF37', color: '#D4AF37', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', fontSize: '20px', padding: '8px 0', marginTop: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{t('pricing.totalCost')}</span>
                      <span style={{ fontSize: '20px' }}>€{(roundedFramePriceHT + roundedShippingCostHT).toFixed(2)}</span>
                    </div>

                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                      {t('pricing.pricesExcludeVat')}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Error Message Display */}
            {checkoutError && (
              <div style={{
                marginTop: '12px',
                padding: '10px 14px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c33',
                fontSize: '14px'
              }}>
                {checkoutError}
              </div>
            )}
          </div>
        ) : (
          <div className="price-breakdown" style={{
            padding: '12px',
            minWidth: 'unset',
            fontSize: '12px'
          }}>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '6px 0' }}>
              <span>{t('pricing.frameOuter')}</span>
              <span>0 × 0 cm</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '6px 0' }}>
              <span>{t('pricing.frameSurface')}</span>
              <span>0 cm²</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '6px 0' }}>
              <span>{t('pricing.frameWeight')}</span>
              <span>0.00 kg</span>
            </div>

            {/* Frame Price */}
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '6px 0' }}>
              <span>{t('pricing.framePrice')}</span>
              <span style={{ fontWeight: 'bold' }}>€0.00</span>
            </div>

            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '6px 0' }}>
              <span>{t('pricing.boxDimensions')}</span>
              <span>0 × 0 × 0 cm</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '6px 0' }}>
              <span>{t('pricing.boxWeight')}</span>
              <span>0.00 kg</span>
            </div>
            <div className="price-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0' }}>
              <span>Total Shipping:</span>
              <span>0.00 kg</span>
            </div>

            <div className="price-line" style={{ fontWeight: 'bold', fontSize: '20px', borderTop: '2px solid #D4AF37', paddingTop: '12px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ fontSize: '16px' }}>{t('pricing.totalCost')}</span>
              <span style={{ fontSize: '20px' }}>€0.00</span>
            </div>
          </div>
        )}
        </CollapsibleSection>

        <div style={{
          padding: '2px 2px 16px',
          flexShrink: 0
        }}>
          <label htmlFor="whatToFrame" style={{
            display: 'block',
            fontSize: '14px',
            color: '#1f2937',
            marginBottom: '6px',
            fontWeight: '600'
          }}>
            {t('labels.whatToFrame')} <span style={{ color: '#c33' }}>{t('form.required')}</span>
          </label>
          <textarea
            id="whatToFrame"
            ref={whatToFrameRef}
            value={whatToFrame}
            onChange={(e) => setWhatToFrame(e.target.value)}
            placeholder={t('placeholders.whatToFrame')}
            maxLength={500}
            rows={4}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              outline: 'none',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
          />
        </div>

        </div>{/* End scrollable area */}

        {/* Order Button - fixed at bottom of panel */}
        <div style={{
          flexShrink: 0,
          backgroundColor: '#fff',
          paddingTop: '12px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            className="btn btn-primary"
            style={{
              width: '100%',
              height: '50px',
              fontSize: '16px',
              fontWeight: '600',
              backgroundColor: '#D4AF37',
              color: '#1f2937',
              border: 'none',
              borderRadius: '8px',
              cursor: (!widthError && !heightError && hasValidDimensions) ? 'pointer' : 'not-allowed',
              opacity: (!widthError && !heightError && hasValidDimensions) ? 1 : 0.6,
              transition: 'all 0.2s ease'
            }}
            onClick={handleOrderFrame}
            disabled={isCheckoutLoading || widthError || heightError || !hasValidDimensions}
          >
            {isCheckoutLoading
              ? t('buttons.processing')
              : userSelectedCountry
                ? `${t('buttons.orderFrame')} — €${
                    vatCalc
                      ? vatCalc.totalTTC.toFixed(2)
                      : (roundedFramePriceHT + roundedShippingCostHT).toFixed(2)
                  }`
                : t('buttons.orderFrame')
            }
          </button>

          {/* Error Message Display */}
          {checkoutError && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c33',
              fontSize: '14px'
            }}>
              {checkoutError}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Order Confirmation Modal */}
      {showOrderModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }} onClick={() => !isCheckoutLoading && setShowOrderModal(false)}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>{t('modal.confirmOrder')}</h2>
              <button
                onClick={() => setShowOrderModal(false)}
                disabled={isCheckoutLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: isCheckoutLoading ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  lineHeight: '32px'
                }}
              >×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>{t('sections.frameDetails')}</h3>

              <div style={{ backgroundColor: '#f9f9f9', padding: '16px', borderRadius: '4px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '14px' }}>
                  <strong>{t('modal.frameSize')}</strong>
                  <span>{calculations.frameOuterWidth} × {calculations.frameOuterHeight} cm</span>

                  <strong>{t('modal.color')}</strong>
                  <span>{selectedColor.name} ({selectedColor.ral || selectedColor.hex})</span>

                  <strong>{t('modal.finish')}</strong>
                  <span>{finish.charAt(0).toUpperCase() + finish.slice(1)}</span>

                  <strong>{t('modal.thickness')}</strong>
                  <span>{thickness.toFixed(1)} cm</span>

                  <strong>{t('modal.whatToFrame')}</strong>
                  <span>{whatToFrame}</span>

                  <strong>{t('modal.weight')}</strong>
                  <span>{calculations.frameWeight.toFixed(2)} kg</span>

                  <strong>{t('modal.delivery')}</strong>
                  <span>{country}{postalCode ? `, ${postalCode}` : ''}</span>

                  {vatCalc && (
                    <>
                      <strong style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>{t('modal.totalPrice')}</strong>
                      <span style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd', fontSize: '20px', fontWeight: 'bold', color: '#D4AF37' }}>
                        €{vatCalc.totalTTC.toFixed(2)} {vatCalc.displayVAT ? 'TTC' : 'HT'}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '16px' }}>{t('sections.contactInformation')}</h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  {t('labels.email')} <span style={{ color: '#c33' }}>*</span>
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder={t('placeholders.email')}
                  disabled={isCheckoutLoading}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  {t('labels.fullName')} <span style={{ color: '#c33' }}>*</span>
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t('placeholders.fullName')}
                  disabled={isCheckoutLoading}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <h3 style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px' }}>{t('sections.shippingAddress')}</h3>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  {t('labels.addressLine1')} <span style={{ color: '#c33' }}>*</span>
                </label>
                <input
                  type="text"
                  value={shippingAddress1}
                  onChange={(e) => setShippingAddress1(e.target.value)}
                  placeholder={t('placeholders.addressLine1')}
                  disabled={isCheckoutLoading}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  {t('labels.addressLine2')}
                </label>
                <input
                  type="text"
                  value={shippingAddress2}
                  onChange={(e) => setShippingAddress2(e.target.value)}
                  placeholder={t('placeholders.addressLine2')}
                  disabled={isCheckoutLoading}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    {t('labels.city')} <span style={{ color: '#c33' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={shippingCity}
                    onChange={(e) => setShippingCity(e.target.value)}
                    placeholder={t('placeholders.city')}
                    disabled={isCheckoutLoading}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    {t('labels.postalCodeAddress')} <span style={{ color: '#c33' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={shippingZip}
                    onChange={(e) => setShippingZip(e.target.value)}
                    placeholder={t('placeholders.zipCode')}
                    disabled={isCheckoutLoading}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px', fontSize: '13px', color: '#666' }}>
                <strong>Country:</strong> {country}
              </div>

              {checkoutError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                  fontSize: '14px',
                  marginBottom: '20px'
                }}>
                  {checkoutError}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowOrderModal(false)}
                disabled={isCheckoutLoading}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  cursor: isCheckoutLoading ? 'not-allowed' : 'pointer',
                  opacity: isCheckoutLoading ? 0.5 : 1
                }}
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={proceedToCheckout}
                disabled={isCheckoutLoading}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  border: 'none',
                  backgroundColor: '#000',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: isCheckoutLoading ? 'not-allowed' : 'pointer',
                  opacity: isCheckoutLoading ? 0.7 : 1
                }}
              >
                {isCheckoutLoading ? t('buttons.processing') : t('buttons.continueCheckout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// SHIPPING CALCULATION FUNCTIONS

// Check if country is supported by Colissimo
function isColissimoSupported(country) {
  for (const zone of ALL_ZONES) {
    if (zone.countries.includes(country)) {
      return true;
    }
  }
  return false;
}

// Check if country is supported by DHL
function isDHLSupported(country) {
  return DHL_ZONES[country] !== undefined;
}

function getShippingZone(country) {
  for (const zone of ALL_ZONES) {
    if (zone.countries.includes(country)) {
      return zone.zone;
    }
  }
  return null; // Country not supported
}

function getApplicableRate(zone, country, weight) {
  // Round weight UP to next available bracket
  const weightBrackets = [0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];
  let applicableWeight = weightBrackets.find(bracket => weight <= bracket);
  if (!applicableWeight) applicableWeight = 30; // Max weight

  // Select appropriate rate table
  let rateTable;

  switch (zone) {
    case 'FRANCE':
      rateTable = FRANCE_RATES;
      break;
    case 'OM1':
      rateTable = OM1_RATES;
      break;
    case 'OM2':
      rateTable = OM2_RATES;
      break;
    case 'ZONE_1':
      if (country === 'Allemagne') rateTable = ZONE_1_GERMANY_RATES;
      else if (country === 'Belgique') rateTable = ZONE_1_BELGIUM_RATES;
      else rateTable = ZONE_1_OTHER_RATES;
      break;
    case 'ZONE_2':
      if (country === 'Royaume-Uni') rateTable = ZONE_2_UK_RATES;
      else if (country === 'Italie') rateTable = ZONE_2_ITALY_RATES;
      else if (country === 'Espagne') rateTable = ZONE_2_SPAIN_RATES;
      else rateTable = ZONE_2_OTHER_RATES;
      break;
    case 'ZONE_3':
      if (country === 'Suisse') rateTable = ZONE_3_SWITZERLAND_RATES;
      else rateTable = ZONE_3_OTHER_RATES;
      break;
    case 'ZONE_4':
      rateTable = ZONE_4_RATES;
      break;
    case 'ZONE_5':
      if (country === 'États-Unis') rateTable = ZONE_5_USA_RATES;
      else if (country === 'Chine') rateTable = ZONE_5_CHINA_RATES;
      else rateTable = ZONE_5_OTHER_RATES;
      break;
    case 'ZONE_6':
    default:
      rateTable = ZONE_6_RATES;
      break;
  }

  return rateTable[applicableWeight] || rateTable[30];
}

function checkIfMechanizable(boxWidth, boxHeight, boxThickness, weight) {
  const { minLength, maxLength, minWidth, maxWidth, minHeight, maxHeight, maxWeight } = MECHANIZABLE_LIMITS;

  return (
    boxWidth >= minLength && boxWidth <= maxLength &&
    boxHeight >= minWidth && boxHeight <= maxWidth &&
    boxThickness >= minHeight && boxThickness <= maxHeight &&
    weight <= maxWeight
  );
}

function calculateShippingCost(totalWeight, country, boxDimensions) {
  const zone = getShippingZone(country);

  // Return null if country not supported by Colissimo
  if (zone === null) {
    return null;
  }

  // Colissimo max weight is 30kg - return null if exceeded
  if (totalWeight > COLISSIMO_LIMITS.maxWeight) {
    return null;
  }

  // Colissimo dimension limits: L+W+H <= 150cm, longest side <= 100cm
  const sumDimensions = boxDimensions.width + boxDimensions.height + boxDimensions.thickness;
  const longestSide = Math.max(boxDimensions.width, boxDimensions.height, boxDimensions.thickness);

  if (sumDimensions > COLISSIMO_LIMITS.maxSumDimensions || longestSide > COLISSIMO_LIMITS.maxLongestSide) {
    return null;
  }

  const baseRate = getApplicableRate(zone, country, totalWeight);

  let additionalFees = FEES.DECARBONATION; // Always add
  let countrySpecific = 0;

  // Check mechanizability
  const isMechanizable = checkIfMechanizable(
    boxDimensions.width,
    boxDimensions.height,
    boxDimensions.thickness,
    totalWeight
  );

  if (!isMechanizable) {
    additionalFees += FEES.NON_MECHANIZABLE;
  }

  // Add country-specific supplements
  if (country === 'Royaume-Uni') countrySpecific = FEES.UK_SUPPLEMENT;
  else if (country === 'États-Unis') countrySpecific = FEES.USA_SUPPLEMENT;
  else if (country === 'Chine') countrySpecific = FEES.CHINA_SUPPLEMENT;

  additionalFees += countrySpecific;

  const totalShippingCost = baseRate + additionalFees;

  return {
    zone,
    baseRate,
    fees: {
      decarbonation: FEES.DECARBONATION,
      nonMechanizable: !isMechanizable ? FEES.NON_MECHANIZABLE : 0,
      countrySpecific
    },
    total: totalShippingCost
  };
}

/**
 * Get DHL rate for a given weight and zone
 * @param {string|number} zone - DHL zone ('A' for domestic, 1-8 for international)
 * @param {number} weight - Chargeable weight in kg
 * @returns {number} Shipping rate in EUR
 */
function getDHLRate(zone, weight) {
  // Round weight up to next 0.5kg for international, 1kg for domestic
  let rateTable;
  let roundedWeight;

  if (zone === 'A') {
    // Domestic France - rates are per 1kg
    rateTable = DHL_DOMESTIC_RATES;
    roundedWeight = Math.ceil(weight);
    if (roundedWeight < 1) roundedWeight = 1;
    if (roundedWeight > 70) roundedWeight = 70;
  } else {
    // International - rates are per 0.5kg up to certain weights
    rateTable = DHL_EXPRESS_RATES[zone];
    if (!rateTable) return null;

    // Round up to next 0.5kg
    roundedWeight = Math.ceil(weight * 2) / 2;
    if (roundedWeight < 0.5) roundedWeight = 0.5;
    if (roundedWeight > 70) roundedWeight = 70;
  }

  // Find the applicable rate
  if (rateTable[roundedWeight] !== undefined) {
    return rateTable[roundedWeight];
  }

  // If exact weight not found, find nearest higher weight
  const weights = Object.keys(rateTable).map(Number).sort((a, b) => a - b);
  for (const w of weights) {
    if (w >= roundedWeight) {
      return rateTable[w];
    }
  }

  // Return highest rate if weight exceeds all
  return rateTable[weights[weights.length - 1]];
}

/**
 * Calculate DHL Express shipping cost
 * @param {number} totalWeight - Actual weight in kg
 * @param {string} country - Destination country name
 * @param {object} boxDimensions - Box dimensions {width, height, thickness} in cm
 * @returns {object} DHL shipping calculation details
 */
function calculateDHLShippingCost(totalWeight, country, boxDimensions) {
  const zone = DHL_ZONES[country];

  if (zone === undefined) {
    return null; // Country not supported by DHL
  }

  // Calculate volumetric weight: (L×W×H)/5000
  const volumetricWeight = (boxDimensions.width * boxDimensions.height * boxDimensions.thickness) / 5000;

  // Chargeable weight is the greater of actual and volumetric
  const chargeableWeight = Math.max(totalWeight, volumetricWeight);

  // Get base rate
  const baseRate = getDHLRate(zone, chargeableWeight);

  if (baseRate === null) {
    return null;
  }

  return {
    zone: zone === 'A' ? 'France' : `Zone ${zone}`,
    baseRate,
    actualWeight: totalWeight,
    volumetricWeight,
    chargeableWeight,
    total: baseRate,
    carrier: 'DHL Express'
  };
}

/**
 * Check if Schenker is available for this shipment
 * @param {string} country - Destination country
 * @param {number} weight - Shipment weight in kg
 * @returns {boolean}
 */
function isSchenkerSupported(country, weight) {
  // Schenker supports France and most European countries
  if (country === 'France') return true;
  if (SCHENKER_EUROPE_RATES[country]) return true;
  return false;
}

/**
 * Get Schenker France zone based on postal code
 * @param {string} postalCode - French postal code (first 2 digits = department)
 * @returns {number} Zone number (1-4)
 */
function getSchenkerFranceZone(postalCode) {
  if (!postalCode || postalCode.length < 2) return 3; // Default to Zone 3

  const dept = postalCode.substring(0, 2);

  if (SCHENKER_FRANCE_ZONE_1.includes(dept)) return 1;
  if (SCHENKER_FRANCE_ZONE_2.includes(dept)) return 2;
  if (SCHENKER_FRANCE_ZONE_4.includes(dept)) return 4;
  return 3; // Default Zone 3 (most of France)
}

/**
 * Calculate Schenker shipping cost
 * @param {number} totalWeight - Actual weight in kg
 * @param {string} country - Destination country
 * @param {object} boxDimensions - Box dimensions {width, height, thickness} in cm
 * @param {string} postalCode - Postal code (required for France zones)
 * @returns {object|null} Schenker shipping calculation details
 */
function calculateSchenkerShippingCost(totalWeight, country, boxDimensions, postalCode = '') {
  // Check if country is supported
  if (!isSchenkerSupported(country, totalWeight)) {
    return null;
  }

  // Calculate volumetric weight (Schenker uses 250 kg/m³)
  const volumeM3 = (boxDimensions.width * boxDimensions.height * boxDimensions.thickness) / 1000000;
  const volumetricWeight = volumeM3 * 250;

  // Chargeable weight is the greater of actual and volumetric
  const chargeableWeight = Math.max(totalWeight, volumetricWeight);

  let baseRate = 0;
  let zone = '';
  let fees = {
    contributionSurete: 0,
    contributionEnergie: SCHENKER_FEES.CONTRIBUTION_ENERGIE,
    livraisonParticulier: SCHENKER_FEES.LIVRAISON_PARTICULIER, // Assuming delivery to private individual
    regionParisienne: 0,
    corseSurcharge: 0
  };

  if (country === 'France') {
    // Domestic France shipping
    const zoneNumber = getSchenkerFranceZone(postalCode);
    zone = `France Zone ${zoneNumber}`;

    // Get rate from rate table
    const zoneRates = SCHENKER_FRANCE_RATES[zoneNumber];

    // Find applicable weight bracket
    const weightBrackets = Object.keys(zoneRates).map(Number).sort((a, b) => a - b);
    let applicableBracket = weightBrackets.find(bracket => chargeableWeight <= bracket);

    if (applicableBracket) {
      baseRate = zoneRates[applicableBracket];
    } else if (chargeableWeight <= 100) {
      // Use the 100kg rate for weights between last bracket and 100kg
      baseRate = zoneRates[100];
    } else {
      // For heavier shipments (>100kg), use per 100kg rates
      const per100kgRates = SCHENKER_FRANCE_PER_100KG[zoneNumber];
      const per100kgBrackets = Object.keys(per100kgRates).map(Number).sort((a, b) => a - b);
      const per100kgBracket = per100kgBrackets.find(bracket => chargeableWeight <= bracket);
      const per100kgRate = per100kgRates[per100kgBracket] || per100kgRates[3000];
      baseRate = (chargeableWeight / 100) * per100kgRate;
    }

    // Add domestic fees
    fees.contributionSurete = SCHENKER_FEES.CONTRIBUTION_SURETE;

    // Check for Paris region surcharge (75-77-78-91-92-93-94-95)
    if (postalCode && ['75', '77', '78', '91', '92', '93', '94', '95'].includes(postalCode.substring(0, 2))) {
      fees.regionParisienne = SCHENKER_FEES.REGION_PARISIENNE;
    }

    // Check for Corsica surcharge (20)
    if (postalCode && postalCode.substring(0, 2) === '20') {
      fees.corseSurcharge = baseRate * SCHENKER_FEES.CORSE_SURCHARGE;
    }

  } else {
    // International shipping
    const countryRates = SCHENKER_EUROPE_RATES[country];
    if (!countryRates) return null;

    zone = `Europe - ${country}`;

    // Find applicable weight bracket
    const weightBrackets = Object.keys(countryRates)
      .filter(k => k !== 'per100kg')
      .map(Number)
      .sort((a, b) => a - b);

    const applicableBracket = weightBrackets.find(bracket => chargeableWeight <= bracket);

    if (applicableBracket) {
      baseRate = countryRates[applicableBracket];
    } else if (chargeableWeight <= 500) {
      // Use 500kg rate
      baseRate = countryRates[500];
    } else {
      // For heavier shipments, use per 100kg rate
      baseRate = (chargeableWeight / 100) * countryRates.per100kg;
    }

    // Add international fees
    fees.contributionSurete = SCHENKER_FEES.CONTRIBUTION_SURETE_INTL;
  }

  // Calculate total
  const totalFees = fees.contributionSurete + fees.contributionEnergie +
                    fees.livraisonParticulier + fees.regionParisienne + fees.corseSurcharge;
  const total = baseRate + totalFees;

  return {
    zone,
    baseRate,
    actualWeight: totalWeight,
    volumetricWeight,
    chargeableWeight,
    fees,
    total,
    carrier: 'DB Schenker'
  };
}

/**
 * Calculate VAT based on destination country
 * @param {number} framePriceHT - Frame price excluding VAT
 * @param {number} shippingCostHT - Shipping cost excluding VAT
 * @param {string} country - Destination country
 * @returns {object} VAT calculation details
 */
function calculateVAT(framePriceHT, shippingCostHT, country) {
  const subtotalHT = framePriceHT + shippingCostHT;

  // Apply VAT only for EU countries
  if (isEUCountry(country)) {
    const rawFrameVatAmount = framePriceHT * FRAME_VAT_RATE;
    const rawShippingVatAmount = shippingCostHT * SHIPPING_VAT_RATE;
    const frameVatAmount = roundToNearestTen(rawFrameVatAmount);
    const shippingVatAmount = roundToNearestTen(rawShippingVatAmount);
    const vatAmount = frameVatAmount + shippingVatAmount;
    const rawTotalTTC = subtotalHT + rawFrameVatAmount + rawShippingVatAmount;
    return {
      framePriceHT,
      shippingCostHT,
      subtotalHT,
      frameVatRate: FRAME_VAT_RATE,
      shippingVatRate: SHIPPING_VAT_RATE,
      rawFrameVatAmount,
      rawShippingVatAmount,
      frameVatAmount,
      shippingVatAmount,
      vatAmount,
      rawTotalTTC,
      totalTTC: subtotalHT + vatAmount,
      displayVAT: true,
      displayFrameVAT: true,
      displayShippingVAT: true,
      note: 'French VAT included'
    };
  }

  const rawShippingVatAmount = shippingCostHT * SHIPPING_VAT_RATE;
  const shippingVatAmount = roundToNearestTen(rawShippingVatAmount);

  // Non-EU/export: no frame VAT, but transport VAT applies.
  return {
    framePriceHT,
    shippingCostHT,
    subtotalHT,
    frameVatRate: 0,
    shippingVatRate: SHIPPING_VAT_RATE,
    frameVatAmount: 0,
    rawShippingVatAmount,
    shippingVatAmount,
    vatAmount: shippingVatAmount,
    rawTotalTTC: subtotalHT + rawShippingVatAmount,
    totalTTC: subtotalHT + shippingVatAmount,
    displayVAT: true,
    displayFrameVAT: false,
    displayShippingVAT: true,
    note: 'Export - No frame VAT. Transport VAT included. Local taxes may apply at destination.'
  };
}

/**
 * Round to nearest 0.5
 * Examples:
 * 124.24 → 124.0
 * 124.32 → 124.5
 * 124.59 → 124.5
 * 124.77 → 125.0
 * 157.08 → 157.0
 */
function roundToNearestHalf(num) {
  return Math.round(num * 2) / 2;
}

function calculateFrame(width, height) {
  // CONSTANTS
  const PHI = 1.618;
  const PHI_4 = Math.pow(PHI, 4); // = 6.854 (Phi to the power of 4)
  const FRAME_WEIGHT_PER_M2 = 15; // kg/m²
  const BOARD_CELLS_WEIGHT_PER_M2 = 1.848; // kg/m²
  const CHIPBOARD_WEIGHT_PER_M2 = 14.547; // kg/m²
  const BOX_MARGIN = 8; // cm added to each side
  const BOX_THICKNESS = 8; // cm (constant)
  const CHIPBOARD_WALL_THICKNESS = 0.044; // m (4.4 cm)
  const PACKAGING_OVERHEAD = 1.08; // 8% for packaging and accessories

  // STEP 1: Check custom lookup table first
  const lookupKey = `${width}×${height}`;
  const customSize = CUSTOM_FRAME_SIZES[lookupKey];

  let frameOuterWidth, frameOuterHeight;

  if (customSize) {
    // Use custom size from lookup table
    const [outerW, outerH] = customSize.split('×').map(Number);
    frameOuterWidth = outerW;
    frameOuterHeight = outerH;
  } else {
    // Calculate using PHI^4 formula
    // Formula: minDimension / (PHI^4)
    // This divides by Phi 4 times: N ÷ Phi ÷ Phi ÷ Phi ÷ Phi = N ÷ (Phi^4)
    const minDimension = Math.min(width, height);
    const frameWidthRaw = minDimension / PHI_4;
    const frameWidth = Math.round(frameWidthRaw);

    // Frame outer dimensions - same frame width on all 4 sides
    frameOuterHeight = height + (frameWidth * 2);
    frameOuterWidth = width + (frameWidth * 2);
  }

  // STEP 2: Calculate frame weight
  const fullBoardArea = frameOuterHeight * frameOuterWidth; // cm²
  const artworkArea = height * width; // cm²
  const frameSurfaceArea = fullBoardArea - artworkArea; // cm²
  const frameWeight = (frameSurfaceArea / 10000) * FRAME_WEIGHT_PER_M2; // kg

  // STEP 3: Calculate box dimensions
  const boxOuterHeight = frameOuterHeight + BOX_MARGIN; // cm
  const boxOuterWidth = frameOuterWidth + BOX_MARGIN; // cm
  const boxThickness = BOX_THICKNESS; // cm

  // STEP 4: Calculate box weight
  // A) Board cells (base/cover)
  const boxAreaM2 = (boxOuterHeight/100) * (boxOuterWidth/100) * 2; // m²
  const boardCellsWeight = boxAreaM2 * BOARD_CELLS_WEIGHT_PER_M2; // kg

  // B) Chipboard (side walls)
  const boxHeightM = BOX_THICKNESS / 100; // convert to meters

  const longSidesArea = boxHeightM * (boxOuterWidth/100) * 2; // m²
  const shortSidesArea = boxHeightM * ((boxOuterHeight/100) - CHIPBOARD_WALL_THICKNESS) * 2; // m²

  const chipboardAreaM2 = longSidesArea + shortSidesArea; // m²
  const chipboardWeight = chipboardAreaM2 * CHIPBOARD_WEIGHT_PER_M2; // kg

  // C) Total box weight with packaging
  const rawBoxWeight = boardCellsWeight + chipboardWeight;
  const totalBoxWeight = rawBoxWeight * PACKAGING_OVERHEAD; // +8%

  // STEP 5: Total shipping weight
  const totalShippingWeight = frameWeight + totalBoxWeight; // kg

  // STEP 5: Calculate frame price
  const framePriceHT = frameSurfaceArea * FRAME_PRICE_PER_CM2; // EUR

  return {
    frameOuterWidth,
    frameOuterHeight,
    frameSurfaceArea,
    frameWeight,
    framePriceHT,
    boxOuterWidth,
    boxOuterHeight,
    boxThickness,
    totalBoxWeight,
    totalShippingWeight
  };
}
